// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/// <reference lib="dom" />

import {
    parseJsonWithNativeSafeNumbers,
    parseLosslessJson,
    stringifyLosslessJson,
} from './lossless-json-codec';
import { z } from 'zod';
import { createLegalSurfacingApi } from './legal-surfacing-api';
import { isInternalRole } from '@pillar-portal/database/dist/frontend';
import type { SafeParseResult } from './zod-utils';
import {
    ApiError,
    createPillarApiRequestScheduler,
    cancelUnreadPillarApiResponse,
    extractErrorMessage,
    type ApiResponseValidationError,
    type ApiTraceHeaderValidationError,
    type ApiValidationError,
    type FetchImplementation,
    type FetchRequestInit,
    type HttpRequestHeaders,
    type PillarApiClientOptions,
    type PillarApiRequestOptions,
} from './pillar-api-core';
import type {
    CloverOAuthAuthorizationStatus,
    CloverPaymentsInfrastructureStatus,
    CloverPaymentsInfrastructureSynchronizationResult,
    CloverReadinessResult,
    RenewCloverOAuthAuthorizationResponse,
    UpdateCloverOAuthRefreshIntervalRequest,
} from './types/ipg/clover/clover-heartbeat.types';
import {
    CloverOAuthAuthorizationStatusSchema,
    CloverPaymentsInfrastructureRequestSchema,
    CloverPaymentsInfrastructureStatusSchema,
    CloverPaymentsInfrastructureSynchronizationResultSchema,
    CloverReadinessResultSchema,
    RenewCloverOAuthAuthorizationRequestSchema,
    RenewCloverOAuthAuthorizationResponseSchema,
    UpdateCloverOAuthRefreshIntervalRequestSchema,
} from './schemas/ipg/clover/clover-heartbeat.schemas';
import type { CloverWebhookProcessingResult } from './types/ipg/clover/clover-webhook.types';
import { CloverWebhookProcessingResultSchema } from './schemas/ipg/clover/clover-webhook.schemas';
import {
    TerminalCommandReadinessRequestOperation,
    TerminalCommandReadinessResourceKind,
    TerminalCommandReadinessVersion,
    type TerminalCommandReadinessChecklistPageRequestV1,
    type TerminalCommandReadinessPageV1,
    type TerminalCommandReadinessResultV1,
} from './types/ipg/terminal-command-readiness.types';
import {
    TerminalCommandReadinessPageV1Schema,
    TerminalCommandReadinessRequestV1Schema,
    TerminalCommandReadinessResultV1Schema,
} from './schemas/ipg/terminal-command-readiness.schemas';
import type { ProcessCloverCardNotPresentPaymentRequest } from './types/ipg/clover/clover-card-not-present-payment.types';
import { ProcessCloverCardNotPresentPaymentRequestSchema } from './schemas/ipg/clover/clover-card-not-present-payment.schemas';

type FetchArguments = [input: string | URL, init?: FetchRequestInit];

export {
    ApiError,
    extractErrorMessage,
    getHttpFailureMessage,
    isPillarApiUnreachableError,
} from './pillar-api-core';
export type {
    ApiResponseValidationError,
    ApiTraceHeaderValidationError,
    ApiValidationError,
    FetchImplementation,
    FetchRequestInit,
    HttpRequestHeaders,
    PillarApiClientOptions,
    PillarApiRequestOptions,
} from './pillar-api-core';

export interface GetProfileRequestOptions {
    readonly handle401AsSessionExpired?: boolean;
}

function isFormDataBody(value: unknown): value is FormData {
    return value instanceof FormData;
}

function isMutatingHttpMethod(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}

function generateIdempotencyKey(): string {
    const cryptoObject = globalThis.crypto;
    const randomUuidParseResult = z
        .function({
            input: [],
            output: z.string(),
        })
        .safeParse(cryptoObject.randomUUID.bind(cryptoObject));
    if (randomUuidParseResult.success) {
        return randomUuidParseResult.data();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Import all types and schemas needed for API functions
import type {
    ActiveRoleAssignmentResponse,
    ActiveRoleAssignmentOperationalResponse,
    CheckProposedPasswordRequest,
    CheckProposedPasswordResponse,
    CancelNativeAppLoginLinkRequest,
    CompleteCustomerPaymentLinkAttachmentRequest,
    FirstLoginRequest,
    FirstLoginResponse,
    LoginRequest,
    NativeAppLoginLinkResponse,
    PasswordlessLoginCodeVerificationRequest,
    PasswordlessLoginLinkExchangeRequest,
    PasswordlessLoginRequest,
    PasswordlessLoginChallengeResponse,
    SessionUserProfileDetailedResponse,
    SwapTokenForCookieRequest,
    SwapTokenForCookieResponse,
    SwapCookieForTokenResponse,
    VerifyTotpRequest,
    VerifyEmailRequest,
    VerifyEmailResponse,
    CreateCustomerSelfOnboardingRequest,
    CreateCustomerSelfOnboardingResponse,
    RequestPasswordResetRequest,
    RequestPasswordResetResponse,
    ValidatePasswordResetTokenRequest,
    ValidatePasswordResetTokenResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    ChangePasswordRequest,
    SetupTotpResponse,
    EnableTotpRequest,
    GenerateBackupCodesResponse,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentResponse,
    SetActiveRoleAssignmentUuidRequest,
    SelectableRoleAssignmentsResponse,
    StartImpersonationRequest,
    ImpersonationContextResponse,
    SendVerificationEmailResponse,
    SingleUseTokensSearchRequest,
    ListSingleUseTokensResponse,
    MfaActivationRequestResponse,
    MfaLoginChallengeResponse,
    MfaSetupStatusResponse,
    MfaPolicyStatusResponse,
    VerifyMfaActivationRequest,
    VerifyMfaLoginRequest,
    VerifyMfaLoginResponse,
} from './auth.types';
import type {
    ProvisionOrganizationalUserRequest,
    ProvisionedUser,
    ProvisionUserRequest,
    LoginInvitationAdministrationState,
    ResendAllLoginInvitationsPage,
    ResendAllLoginInvitationsRequest,
    UpdateUserRequest,
    UpdateUserResponse,
    UpdateProfileRequest,
    UserProfileDetailedPublicResponse,
    DeleteMyAccountRequest,
    DeleteMyAccountResponse,
    AssignRoleRequest,
    RevokeRoleRequest,
    UpdateRoleAssignmentRequest,
    ResetPasswordAdminRequest,
    RequestPasswordResetAdminRequest,
    MfaResetRequest,
    MfaResetResponse,
    EmailAuthenticationAttemptLimitResetRequest,
    SetUserMfaRequiredRequest,
    RequestAddEmailVerificationRequest,
    RequestAddEmailVerificationResponse,
    RequestAddPhoneVerificationRequest,
    RequestAddPhoneVerificationResponse,
    SetUserEmailsRequest,
    SetUserPhonesRequest,
    VerifyAddPhoneRequest,
} from './user.types';
import type {
    ApplyOrganizationUsersCsvImportRequest,
    ApplyOrganizationUsersCsvImportResponse,
    CreateOrganizationDocumentTemplateRequest,
    CreateIsoRequest,
    CreateIsoResponse,
    CreateMerchantRequest,
    CreateMerchantResponse,
    AssignMerchantToIsoRequest,
    SetOrganizationParentRequest,
    UpdateOrganizationRequest,
    ResolveBusinessTimeZoneRequest,
    ResolveBusinessTimeZoneResponse,
    GetOrganizationResponse,
    AssignMerchantToIsoResponse,
    SetOrganizationParentResponse,
    UpdateOrganizationResponse,
    ExportMerchantBackupRequest,
    ExportMerchantBackupResponse,
    GetMerchantChecklistResponse,
    GetMerchantChecklistCheckResponse,
    ListOrganizationDocumentTemplatesRequest,
    ListOrganizationDocumentTemplatesResponse,
    MerchantChecklistCheckCode,
    OrganizationDocumentTemplateOverrideDescriptor,
    RestoreMerchantBackupDryRunRequest,
    RestoreMerchantBackupDryRunResponse,
    RestoreMerchantBackupApplyRequest,
    RestoreMerchantBackupApplyResponse,
    PreviewOrganizationUsersCsvImportRequest,
    PreviewOrganizationUsersCsvImportResponse,
    SetOrganizationDocumentTemplateActiveRequest,
    UpdateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateResponse,
} from './organization.types';
import type { RpaModuleName } from './backend_config.types';
import {
    SambaWorkflowReleaseQueryV2Schema,
    type SambaWorkflowReleaseQueryV2,
} from './samba-workflow-release';
import type {
    OrganizationOmnisearchSearchRequest,
    OrganizationOmnisearchPageResponse,
    UserOmnisearchPageRequest,
    UserOmnisearchPageResponse,
    ListSubsidiaryIsosPageResponse,
    ListSubsidiaryMerchantsPageResponse,
    ListDirectOrganizationUsersPageResponse,
} from './omnisearch.types';
import type {
    PublicUserListingSearchRequest,
    PublicUserListingPageResponse,
} from './user-listing.types';
import type {
    AcceptLegalDocumentRequest,
    AcceptApplicableTermsRequest,
    AcceptOrganizationAgreementRequest,
    ApplicableTermsResult,
    CreateLegalDocumentAssignmentRequest,
    LegalDocumentAssignmentPublic,
    OrganizationAgreementStatus,
    SearchOrganizationAgreementsPageResponse,
    CreateLegalDocumentRequest,
    CreateLegalDocumentResponse,
    GetLegalDocumentResponse,
    LegalDocumentsListRequest,
    LegalDocumentsSearchRequest,
    ListPendingRequiredLegalDocumentsResponse,
    SearchLegalDocumentsPageResponse,
    SearchLegalDocumentAssignmentsPageResponse,
    UpdateLegalDocumentStatusRequest,
    UpdateLegalDocumentRequest,
    RevokeAllLegalDocumentAssignmentsRequest,
    RevokeAllLegalDocumentAssignmentsResponse,
    RevokeLegalDocumentAssignmentRequest,
    SearchLegalDocumentAcceptancesPageResponse,
    GetLegalDocumentAcceptanceResponse,
} from './legal-documents.types';
import {
    LegalAgreementImpactRequestSchema,
    LegalAgreementImpactSchema,
    LegalConfigurationHealthSchema,
    type LegalAgreementImpact,
    type LegalAgreementImpactRequest,
    type LegalConfigurationHealth,
} from './legal-agreement-administration.types';
import type {
    LegalDocumentAcceptancesSearchRequest,
    OutstandingSignaturesSearchRequest,
    SearchOutstandingSignaturesPageResponse,
} from './legal-reporting.types';
import type {
    GetMyLegalAgreementResponse,
    MyLegalAgreementsSearchRequest,
    SearchMyLegalAgreementsPageResponse,
} from './my-legal-agreements.types';
import type {
    ForensicOptionSearchRequest,
    ListForensicReferenceOptionsPageResponse,
    ListForensicSessionOptionsPageResponse,
    ListRecentRoleActivityPageResponse,
    ListRecentUserActivityPageResponse,
    ListTransactionLogDisplayPageResponse,
    ListTransactionLogPageResponse,
    CaptureCredentialAuditBaselineResponse,
    RevealHistoricalCredentialFieldRequest,
    RevealHistoricalCredentialFieldResponse,
    RecentRoleActivitySearchRequest,
    RecentUserActivitySearchRequest,
    TransactionLogSearchRequest,
} from './transaction-log.types';
import type {
    CloudLoggingQueryRequest,
    CloudLoggingOptionSearchRequest,
    ListCloudLoggingEntriesPageResponse,
    ListCloudLoggingOptionsPageResponse,
    LogIngestRequest,
} from './logging.types';
import type {
    CustomerUsersSearchRequest,
    CustomerUsersPageResponse,
    MerchantCustomerRecordDetailsResponse,
    MerchantCustomerRecordIpwLookupRequest,
    MerchantCustomerRecordIpwLookupResponse,
    UpdateMerchantCustomerRecordRequest,
    MerchantCustomerRecordSearchRequest,
    MerchantCustomerRecordSearchPageResponse,
    UpsertIpwMerchantCustomerRecordRequest,
    UpsertIpwMerchantCustomerRecordResponse,
} from './customer.types';
import type {
    ExpireSessionUuidRequest,
    ExpireUserSessionRequest,
    GetSessionsResponse,
    SessionsSearchRequest,
    ListSessionsPageResponse,
    UpdateSessionConsoleRequest,
} from './session.types';
import type {
    CloverRuntimeEnvironmentAvailabilityResponse,
    ProductionPresetAvailabilityResponse,
    EffectiveConfigResponse,
    CombinedConfigResponse,
    NavigateMenuWebsitesResponse,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest,
    UpdateRoleAssignmentConfigRequest,
} from './samba-config.types';
export { InstallerDownloadLinkTarget, NavigateMenuWebsiteLinkTarget } from './samba-config.types';
import type {
    MerchantConfig,
    RoleAssignmentCustomConfig,
    CustomerPortalDeliveryChannel,
} from './backend_config.types';
import { getEnabledPaymentRequestDeliveryChannels } from './backend_config.types';
import type {
    AwsCredentialsResponse,
    GetCallerIdentityResponse,
    GetSystemInfoResponse,
    GetSesDiagnosticsResponse,
} from './diagnostics.types';
import type {
    ListEncryptionKeyCredentialRotationAttemptsRequest,
    ListEncryptionKeyCredentialRotationAttemptsResponse,
    ListEncryptionKeyCredentialsRequest,
    ListEncryptionKeyCredentialsResponse,
    ListEncryptionKeysRequest,
    ListEncryptionKeysResponse,
    RekeyEncryptionKeyUsageRequest,
    RekeyEncryptionKeyUsageResponse,
    RotateEncryptionKeyResponse,
} from './encryption-key.types';
import type {
    CreateIpxCredentialRequest,
    ListSecretsResponse,
    GetSecretResponse,
    SecretMetadataResponse,
    SecretWithCredentialsResponse,
    CreateSecretRequest,
    CreateSecretResponse,
    CredentialTestRequest,
    CredentialTestResult,
    ListSecretCredentialDefinitionsQuery,
    ListSecretCredentialDefinitionsResponse,
    UpdateSecretRequest,
    CredentialUsagesResponse,
    DisconnectCredentialUsagesRequest,
    DisconnectCredentialUsagesResponse,
    CreateDashboardWebsiteCredentialRequest,
    CredentialRotationBeginRequest,
    CredentialRotationBeginResponse,
    CredentialRotationFailRequest,
    CredentialRotationHistoryRequest,
    CredentialRotationHistoryResponse,
    CredentialRotationInjectedRequest,
    CredentialRotationManualPromotionRequest,
    CredentialRotationManualPromotionResponse,
    CredentialRotationPromoteRequest,
    CredentialRotationPromoteResponse,
    CredentialRotationSubmittedRequest,
    CreateOrganizationIpxCredentialPolicyRequest,
    IpxCredentialDefinitionSearchRequest,
    IpxCredentialDefinitionSearchResponse,
    SearchOrganizationIpxCredentialPolicyDefinitionsRequest,
    SearchOrganizationIpxCredentialPolicyDefinitionsResponse,
    IpxCredentialSummaryListItem,
    IpxCredentialRuntimeBootstrapRequest,
    IpxCredentialRuntimeBootstrapResponse,
    IpxCredentialRuntimeResolveRequest,
    IpxCredentialRuntimeResolveResponse,
    ListDashboardWebsiteCredentialsRequest,
    ListDashboardWebsiteCredentialsResponse,
    ListIpxCredentialsForPolicyResponse,
    ListOrganizationIpxCredentialPoliciesRequest,
    ListOrganizationIpxCredentialPoliciesResponse,
    ManualCredentialCaptureRequest,
    ManualCredentialCaptureResponse,
    OrganizationIpxCredentialPolicyLifecycleRequest,
    OrganizationIpxCredentialPolicyResponse,
    RevealIpxCredentialSecretFieldRequest,
    RevealIpxCredentialSecretFieldResponse,
    RpaBuilderAttachCredentialPolicyRequest,
    RpaBuilderAttachCredentialPolicyResponse,
    RpaBuilderBootstrapRequest,
    RpaBuilderBootstrapResponse,
    RpaBuilderCreateCredentialDefinitionRequest,
    RpaBuilderCreateWebResourceModelRequest,
    RpaBuilderDeleteCredentialDefinitionRequest,
    RpaBuilderDeleteCredentialDefinitionResponse,
    RpaBuilderResetInvalidLoginWorkflowRequest,
    RpaBuilderResetInvalidLoginWorkflowResponse,
    RpaBuilderDeleteWebResourceModelRequest,
    RpaBuilderDeleteWebResourceModelResponse,
    RpaBuilderDeactivateCredentialPolicyRequest,
    RpaBuilderDeactivateCredentialPolicyResponse,
    RpaBuilderFetchWebResourceModelRequest,
    RpaBuilderFetchWebResourceModelResponse,
    RpaBuilderListCredentialDefinitionsRequest,
    RpaBuilderListCredentialDefinitionsResponse,
    RpaBuilderListWebResourceModelsRequest,
    RpaBuilderListWebResourceModelsResponse,
    RpaBuilderReactivateCredentialPolicyRequest,
    RpaBuilderReactivateCredentialPolicyResponse,
    RpaBuilderSaveCredentialDefinitionResponse,
    RpaBuilderSaveWebsiteCredentialConfigurationRequest,
    RpaBuilderSaveWebsiteCredentialConfigurationResponse,
    RpaBuilderSaveWebResourceModelResponse,
    RpaBuilderUpdateCredentialDefinitionRequest,
    RpaBuilderReplaceWebResourceModelRequest,
    UpdateIpxCredentialRequest,
    SecretIdentityReference,
    SecretOrganizationUuidReference,
    SecretOrganizationReference,
    SecretUuidReference,
} from './secret.types';
import {
    RpaBuilderSaveWebsiteCredentialConfigurationRequestSchema,
    RpaBuilderSaveWebsiteCredentialConfigurationResponseSchema,
} from './schemas/rbs-website.schemas';
import {
    CreateDashboardWebsiteCredentialRequestSchema,
    CredentialRotationBeginRequestSchema,
    CredentialRotationBeginResponseSchema,
    CredentialRotationFailRequestSchema,
    CredentialRotationHistoryRequestSchema,
    CredentialRotationHistoryResponseSchema,
    CredentialRotationInjectedRequestSchema,
    CredentialRotationManualPromotionRequestSchema,
    CredentialRotationManualPromotionResponseSchema,
    CredentialRotationPromoteRequestSchema,
    CredentialRotationPromoteResponseSchema,
    CredentialRotationSubmittedRequestSchema,
    CreateIpxCredentialRequestSchema,
    CreateOrganizationIpxCredentialPolicyRequestSchema,
    IpxCredentialDefinitionSearchRequestSchema,
    IpxCredentialDefinitionSearchResponseSchema,
    SearchOrganizationIpxCredentialPolicyDefinitionsRequestSchema,
    SearchOrganizationIpxCredentialPolicyDefinitionsResponseSchema,
    IpxCredentialSummaryListItemSchema,
    IpxCredentialRuntimeBootstrapRequestSchema,
    IpxCredentialRuntimeBootstrapResponseSchema,
    IpxCredentialRuntimeResolveRequestSchema,
    IpxCredentialRuntimeResolveResponseSchema,
    ListDashboardWebsiteCredentialsRequestSchema,
    ListDashboardWebsiteCredentialsResponseSchema,
    ListIpxCredentialsForPolicyResponseSchema,
    ListOrganizationIpxCredentialPoliciesRequestSchema,
    ListOrganizationIpxCredentialPoliciesResponseSchema,
    ManualCredentialCaptureRequestSchema,
    ManualCredentialCaptureResponseSchema,
    OrganizationIpxCredentialPolicyLifecycleRequestSchema,
    OrganizationIpxCredentialPolicyResponseSchema,
    RevealIpxCredentialSecretFieldRequestSchema,
    RevealIpxCredentialSecretFieldResponseSchema,
    RpaBuilderAttachCredentialPolicyRequestSchema,
    RpaBuilderAttachCredentialPolicyResponseSchema,
    RpaBuilderBootstrapRequestSchema,
    RpaBuilderBootstrapResponseSchema,
    RpaBuilderCreateCredentialDefinitionRequestSchema,
    RpaBuilderCreateWebResourceModelRequestSchema,
    RpaBuilderDeleteCredentialDefinitionRequestSchema,
    RpaBuilderDeleteCredentialDefinitionResponseSchema,
    RpaBuilderResetInvalidLoginWorkflowRequestSchema,
    RpaBuilderResetInvalidLoginWorkflowResponseSchema,
    RpaBuilderDeleteWebResourceModelRequestSchema,
    RpaBuilderDeleteWebResourceModelResponseSchema,
    RpaBuilderDeactivateCredentialPolicyRequestSchema,
    RpaBuilderDeactivateCredentialPolicyResponseSchema,
    RpaBuilderFetchWebResourceModelRequestSchema,
    RpaBuilderFetchWebResourceModelResponseSchema,
    RpaBuilderListCredentialDefinitionsRequestSchema,
    RpaBuilderListCredentialDefinitionsResponseSchema,
    RpaBuilderListWebResourceModelsRequestSchema,
    RpaBuilderListWebResourceModelsResponseSchema,
    RpaBuilderReactivateCredentialPolicyRequestSchema,
    RpaBuilderReactivateCredentialPolicyResponseSchema,
    RpaBuilderSaveCredentialDefinitionResponseSchema,
    RpaBuilderSaveWebResourceModelResponseSchema,
    RpaBuilderUpdateCredentialDefinitionRequestSchema,
    RpaBuilderReplaceWebResourceModelRequestSchema,
    ListSecretCredentialDefinitionsQuerySchema,
    ListSecretCredentialDefinitionsResponseSchema,
    SecretOrganizationReferenceSchema,
    SecretOrganizationUuidReferenceSchema,
    SecretUuidReferenceSchema,
    UpdateIpxCredentialRequestSchema,
} from './schemas/secret.schemas';
import type {
    ListPublicBundlesResponse,
    OrganizationPermissionAuditResponse,
    OrganizationScopedPermissionSourceOptionsResponse,
    PermissionsBundlePublic,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponse,
    PolicyPermissionBundleRoleAssignmentsSearchRequest,
    PermissionSourceRoleAssignmentsSearchPageResponse,
    PermissionSourceRoleAssignmentsSearchRequest,
    RoleAssignmentPermissionsView,
    RoleAssignmentPermissionAuditResponse,
    GrantPermissionRequest,
    RevokePermissionRequest,
    RepairAuditedPermissionRequest,
    AssignBundleRequest,
    CreateSharedBundleRequest,
    UpdateBundleRequest,
    RoleEligibility,
    PolicyPermissionBundlesSearchRequest,
    PolicyPermissionBundlesSearchPageResponse,
    CreatePolicyPermissionBundleRequest,
    UpdatePolicyPermissionBundleRequest,
    ListPolicyPermissionGrantsResponse,
    GrantPolicyPermissionBundleRequest,
    ReorderPolicyPermissionGrantsRequest,
} from './permission.types';
import type {
    AllowedWebsiteTemplateBlastRadiusResponse,
    AllowedWebsiteTemplateMutationResponse,
    AllowedWebsiteTemplateSearchPageResponse,
    AllowedWebsiteTemplateSearchRequest,
    AllowedWebsiteTemplateUuidRequest,
    CreateAllowedWebsiteTemplateRequest,
    CreateLocalOrganizationAllowedWebsiteRequest,
    DeleteOrganizationAllowedWebsiteResponse,
    ListOrganizationAllowedWebsitesResponse,
    OrganizationAllowedWebsiteMutationResponse,
    PromoteOrganizationWebsiteToTemplateRequest,
    PromoteOrganizationWebsiteToTemplateResponse,
    ReferenceAllowedWebsiteTemplateToOrganizationRequest,
    SafeDeleteAllowedWebsiteTemplateResponse,
    UpdateAllowedWebsiteTemplateRequest,
    UpdateOrganizationAllowedWebsiteRequest,
} from './allowed-website-template.types';
import {
    OrganizationPermissionAuditResponseSchema,
    RoleAssignmentPermissionAuditResponseSchema,
} from './permission.types';
import type {
    CspViolationReportPayload,
    HealthResponse,
    MessageResponse,
    SambaModuleListResponse,
    SambaVersionResponse,
    SambaInstallerQaQuery,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
} from './common.types';
import type { WindowsArtifactQa } from './windows-artifact-qa.types';
import type {
    GetVaultTransactionsResponse,
    GetTransactionDetailsResponse,
    GatewayTransactionDetailsFetchResult,
    GetReceiptDataResponse,
    GetPaymentAuditLogResponse,
    GetSavedCardPaymentMethodsRequest,
    GetSavedCardPaymentMethodsResponse,
    GetDelegateTokenRequest,
    GetDelegateTokenResponse,
    BinLookupRequest,
    BinLookupResponse,
    GetPaymentConfigurationRequest,
    PaymentConfigurationResponse,
    PaymentActionReadinessResponse,
    PaymentReadinessAction,
    SelectedPaymentActionReadinessResponseV1,
    ListCardPaymentTerminalDevicesRequest,
    ListAccessibleTerminalsInfoRequest,
    ListAccessibleCredentialsInfoRequest,
    ListCardPaymentTerminalDevicesResponse,
    ListAccessibleCredentialsInfoResponse,
    ListAccessibleTerminalsInfoResponse,
    GetReconcilePaymentsCandidatesRequest,
    GetReconcilePaymentsCandidatesResponse,
    RefundTransactionRequest,
    RefundPaymentRequestRequest,
    RefundPaymentRequestResponse,
    RefundTransactionResponse,
    ResetPaymentRequestReconciliationResponse,
    ResetTransactionReconciliationResponse,
    VoidTransactionRequest,
    VoidPaymentRequestResponse,
    VoidTransactionResponse,
    TerminalSaleRequest,
    TerminalSaleResponse,
    TerminalTokenizationRequest,
    TerminalTokenizationResponse,
    TerminalAbortRequest,
    TerminalAbortResponse,
    RecordTokenRequest,
    RecordTokenResponse,
    SubmitCardTokenPaymentRequest,
    ProcessStoredPaymentMethodPaymentRequest,
    ProcessCardTokenPaymentResponse,
    ProcessCardNotPresentPaymentResponse,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    RecordReconciliationRequest,
    RecordReconciliationResponse,
    RecordPaymentRequestReconciliationRequest,
    RecordPaymentRequestReconciliationResponse,
    CreateDraftPaymentRequestRequest,
    CreateDraftPaymentRequestResponse,
    PaymentRequestOwnerCandidatesResponse,
    UploadPaymentRequestDocumentResponse,
    FinalizeSendPaymentRequestRequest,
    FinalizeSendPaymentRequestResponse,
    MerchantPaymentRequestSearchRequest,
    MerchantPaymentRequestListResponse,
    CustomerPaymentMethodListRequest,
    MerchantPaymentMethodTokenListRequest,
    MerchantPaymentMethodTokenDetailsView,
    MerchantPaymentMethodTokenListResponse,
    PaymentRequestDeliveryEventList,
    PaymentRequestDeliveryEventListRequest,
    PaymentRequestDetailsView,
    CustomerPaymentMethodListResponse,
    CustomerPaymentRequestDetailsRequest,
    MerchantPaymentRequestDetailsRequest,
    CustomerPaymentMethodBootstrapRequest,
    CustomerPaymentMethodBootstrapResponse,
    CustomerPaymentMethodBinLookupRequest,
    CreateCustomerPaymentMethodRequest,
    CreateCustomerPaymentMethodResponse,
    CustomerPortalDashboardRequest,
    CustomerPortalDashboardResponse,
    CustomerPaymentHistoryRequest,
    CustomerPaymentHistoryResponse,
    CustomerPaymentRequestSearchRequest,
    CustomerPaymentRequestListResponse,
    GetPaymentAuditLogSearchRequest,
    GetVaultTransactionsSearchRequest,
    PaymentTransactionLifecycleEventSearchRequest,
    PaymentTransactionLifecycleEventListResponse,
    ResendPaymentRequestRequest,
    UpdateCustomerPaymentMethodRequest,
    UpdateCustomerPaymentMethodResponse,
    DeleteCustomerPaymentMethodRequest,
    DeleteCustomerPaymentMethodResponse,
    CancelAndReissuePaymentRequestRequest,
    CancelAndReissuePaymentRequestResponse,
    PublicPaymentLinkActionResponse,
    PublicPaymentRequestView,
    GuestPaymentEntryContextView,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentRequestDocumentAccessResponse,
    GuestPaymentSuccessView,
    GuestBinLookupRequest,
    GuestHostedCardTokenQuoteRequest,
    HostedCardTokenQuoteRequest,
    HostedCardTokenQuoteResponse,
    GuestProcessCardTokenPaymentRequest,
    AuthenticatedProcessCardTokenPaymentRequest,
    GuestProcessStoredPaymentMethodPaymentRequest,
    SendgridEventWebhookPayload,
    TwilioStatusCallbackPayload,
} from './payment.types';
import type {
    ProcessorSearchRequest,
    ProcessorSearchPageResponse,
    CreateProcessorRequest,
    GetProcessorResponse,
    UpdateProcessorRequest,
    MerchantAccountSearchRequest,
    MerchantAccountSearchPageResponse,
    CreateMerchantAccountRequest,
    UpdateMerchantAccountRequest,
    GetMerchantAccountResponse,
    LogicalTerminalSearchRequest,
    LogicalTerminalSearchPageResponse,
    CreateLogicalTerminalRequest,
    UpdateLogicalTerminalRequest,
    GetLogicalTerminalResponse,
    MerchantOrganizationSearchRequest,
    MerchantOrganizationSearchPageResponse,
    PreviewLogicalTerminalOrganizationChangeRequest,
    PreviewLogicalTerminalOrganizationChangeResponse,
    TerminalDeviceSearchRequest,
    TerminalDeviceSearchPageResponse,
    CreateTerminalDeviceRequest,
    UpdateTerminalDeviceRequest,
    GetTerminalDeviceResponse,
} from './payments_infrastructure.types';
import {
    CompleteCustomerPaymentLinkAttachmentRequestSchema,
    CheckProposedPasswordRequestSchema,
    CheckProposedPasswordResponseSchema,
    FirstLoginRequestSchema,
    FirstLoginResponseSchema,
    LoginResponseSchema,
    NativeAppLoginLinkResponseSchema,
    CancelNativeAppLoginLinkRequestSchema,
    PasswordlessLoginChallengeResponseSchema,
    PasswordlessLoginCodeVerificationRequestSchema,
    PasswordlessLoginLinkExchangeRequestSchema,
    PasswordlessLoginRequestSchema,
    SessionUserProfileDetailedResponseSchema,
    ActiveRoleAssignmentResponseSchema,
    VerifyTotpResponseSchema,
    SwapCookieForTokenResponseSchema,
    SwapTokenForCookieRequestSchema,
    SwapTokenForCookieResponseSchema,
    VerifyEmailResponseSchema,
    CreateCustomerSelfOnboardingResponseSchema,
    RequestPasswordResetResponseSchema,
    ValidatePasswordResetTokenResponseSchema,
    ResetPasswordResponseSchema,
    SendVerificationEmailResponseSchema,
    SetupTotpResponseSchema,
    GenerateBackupCodesResponseSchema,
    SetActiveRoleAssignmentResponseSchema,
    SelectableRoleAssignmentsResponseSchema,
    StartImpersonationRequestSchema,
    ImpersonationContextResponseSchema,
    SingleUseTokensSearchRequestSchema,
    ListSingleUseTokensResponseSchema,
    MfaActivationRequestResponseSchema,
    MfaLoginChallengeResponseSchema,
    MfaSetupStatusResponseSchema,
    MfaPolicyStatusResponseSchema,
    VerifyMfaActivationRequestSchema,
    VerifyMfaLoginRequestSchema,
    VerifyMfaLoginResponseSchema,
} from './schemas/auth.schemas';
import {
    ProvisionedUserV2Schema,
    LoginInvitationAdministrationStateV2Schema,
    ResendAllLoginInvitationsPageV2Schema,
    ResendAllLoginInvitationsRequestSchema,
    UserProfileDetailedPublicResponseV2Schema,
    DeleteMyAccountResponseSchema,
    ResetPasswordAdminRequestSchema,
    RequestPasswordResetAdminRequestSchema,
    MfaResetRequestSchema,
    MfaResetResponseSchema,
    EmailAuthenticationAttemptLimitResetRequestSchema,
    SetUserMfaRequiredRequestSchema,
    UpdateUserResponseSchema,
    RequestAddEmailVerificationRequestSchema,
    RequestAddEmailVerificationResponseSchema,
    RequestAddPhoneVerificationRequestSchema,
    RequestAddPhoneVerificationResponseSchema,
    SetUserPhonesRequestSchema,
    UpdateProfileRequestSchema,
    VerifyAddPhoneRequestSchema,
} from './schemas/user.schemas';
import {
    ExpireSessionUuidRequestSchema,
    ExpireUserSessionRequestSchema,
    GetSessionsResponseSchema,
    SessionsSearchRequestSchema,
    ListSessionsPageResponseSchema,
    UpdateSessionConsoleRequestSchema,
} from './schemas/session.schemas';
import {
    ApplyOrganizationUsersCsvImportRequestSchema,
    ApplyOrganizationUsersCsvImportResponseV2Schema,
    CreateOrganizationDocumentTemplateRequestSchema,
    GetOrganizationResponseSchema,
    AssignMerchantToIsoResponseSchema,
    OrganizationDocumentTemplateOverrideDescriptorSchema,
    OrganizationUuidParamSchema,
    SetOrganizationParentResponseSchema,
    UpdateOrganizationResponseSchema,
    ResolveBusinessTimeZoneRequestSchema,
    ResolveBusinessTimeZoneResponseSchema,
    CreateIsoResponseSchema,
    CreateMerchantResponseSchema,
    ExportMerchantBackupRequestSchema,
    ExportMerchantBackupResponseSchema,
    GetMerchantChecklistResponseSchema,
    GetMerchantChecklistCheckResponseSchema,
    ListOrganizationDocumentTemplatesRequestSchema,
    ListOrganizationDocumentTemplatesResponseSchema,
    RestoreMerchantBackupDryRunRequestSchema,
    RestoreMerchantBackupDryRunResponseSchema,
    RestoreMerchantBackupApplyRequestSchema,
    RestoreMerchantBackupApplyResponseSchema,
    PreviewOrganizationUsersCsvImportRequestSchema,
    PreviewOrganizationUsersCsvImportResponseSchema,
    SetOrganizationDocumentTemplateActiveRequestSchema,
    UpdateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateResponseSchema,
} from './schemas/organization.schemas';
import {
    OrganizationOmnisearchSearchRequestSchema,
    OrganizationOmnisearchPageResponseSchema,
    UserOmnisearchPageResponseSchema,
    ListSubsidiaryIsosPageResponseSchema,
    ListSubsidiaryMerchantsPageResponseSchema,
    ListDirectOrganizationUsersPageResponseSchema,
} from './schemas/omnisearch.schemas';
import {
    PublicUserListingPageResponseSchema,
    PublicUserListingSearchRequestSchema,
} from './schemas/user-listing.schemas';
import {
    AcceptLegalDocumentRequestSchema,
    AcceptApplicableTermsRequestSchema,
    AcceptOrganizationAgreementRequestSchema,
    ApplicableTermsResultSchema,
    CreateLegalDocumentAssignmentRequestSchema,
    LegalDocumentAssignmentPublicSchema,
    OrganizationAgreementStatusSchema,
    CreateLegalDocumentRequestSchema,
    CreateLegalDocumentResponseSchema,
    GetLegalDocumentResponseSchema,
    LegalDocumentUuidParamSchema,
    LegalDocumentsListRequestSchema,
    LegalDocumentsSearchRequestSchema,
    ListPendingRequiredLegalDocumentsResponseSchema,
    SearchLegalDocumentsPageResponseSchema,
    UpdateLegalDocumentRequestSchema,
    RevokeAllLegalDocumentAssignmentsRequestSchema,
    RevokeAllLegalDocumentAssignmentsResponseSchema,
    RevokeLegalDocumentAssignmentRequestSchema,
    SearchLegalDocumentAcceptancesPageResponseSchema,
    GetLegalDocumentAcceptanceResponseSchema,
    SearchLegalDocumentAssignmentsPageResponseSchema,
    SearchOrganizationAgreementsPageResponseSchema,
} from './schemas/legal-documents.schemas';
import {
    LegalDocumentAcceptancesSearchRequestSchema,
    OutstandingSignaturesSearchRequestSchema,
    SearchOutstandingSignaturesPageResponseSchema,
} from './schemas/legal-reporting.schemas';
import {
    GetMyLegalAgreementResponseSchema,
    MyLegalAgreementsSearchRequestSchema,
    SearchMyLegalAgreementsPageResponseSchema,
} from './schemas/my-legal-agreements.schemas';
import {
    ForensicOptionSearchRequestSchema,
    ListForensicReferenceOptionsPageResponseSchema,
    ListForensicSessionOptionsPageResponseSchema,
    ListRecentRoleActivityPageResponseSchema,
    ListRecentUserActivityPageResponseSchema,
    ListTransactionLogDisplayPageResponseSchema,
    ListTransactionLogPageResponseSchema,
    CaptureCredentialAuditBaselineResponseSchema,
    RevealHistoricalCredentialFieldRequestSchema,
    RevealHistoricalCredentialFieldResponseSchema,
    RecentRoleActivitySearchRequestSchema,
    RecentUserActivitySearchRequestSchema,
    TransactionLogSearchRequestSchema,
} from './schemas/transaction-log.schemas';
import {
    CustomerUsersSearchRequestSchema,
    CustomerUsersPageResponseSchema,
    MerchantCustomerRecordDetailsResponseSchema,
    MerchantCustomerRecordIpwLookupRequestSchema,
    MerchantCustomerRecordIpwLookupResponseSchema,
    MerchantCustomerRecordSearchRequestSchema,
    MerchantCustomerRecordSearchPageResponseSchema,
    UpsertIpwMerchantCustomerRecordRequestSchema,
    UpsertIpwMerchantCustomerRecordResponseSchema,
    UpdateMerchantCustomerRecordRequestSchema,
} from './schemas/customer.schemas';
import {
    CloverRuntimeEnvironmentAvailabilityResponseSchema,
    ProductionPresetAvailabilityResponseSchema,
    ConfigResponseSchema,
    CombinedConfigResponseSchema,
    EffectiveConfigResponseSchema,
    NavigateMenuWebsitesResponseSchema,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema,
    RoleAssignmentUserConfigResponseSchema,
    UpdateRoleAssignmentConfigRequestSchema,
} from './schemas/samba-config.schemas';
import {
    GetVaultTransactionsResponseSchema,
    GetVaultTransactionsSearchRequestSchema,
    PaymentTransactionLifecycleEventListResponseSchema,
    PaymentTransactionLifecycleEventSearchRequestSchema,
    GetTransactionDetailsResponseSchema,
    GatewayTransactionDetailsFetchResultSchema,
    GetReceiptDataResponseSchema,
    GetPaymentAuditLogResponseSchema,
    GetPaymentAuditLogSearchRequestSchema,
    GetSavedCardPaymentMethodsResponseSchema,
    GetDelegateTokenResponseSchema,
    BinLookupResponseSchema,
    GetPaymentConfigurationRequestSchema,
    PaymentConfigurationResponseSchema,
    PaymentActionReadinessResponseSchema,
    PaymentReadinessActionSchema,
    SelectedPaymentActionReadinessResponseV1Schema,
    ListCardPaymentTerminalDevicesRequestSchema,
    ListAccessibleTerminalsInfoRequestSchema,
    ListAccessibleCredentialsInfoRequestSchema,
    ListCardPaymentTerminalDevicesResponseSchema,
    ListAccessibleCredentialsInfoResponseSchema,
    ListAccessibleTerminalsInfoResponseSchema,
    GetReconcilePaymentsCandidatesResponseSchema,
    RefundPaymentRequestResponseSchema,
    RefundTransactionResponseSchema,
    ResetPaymentRequestReconciliationResponseSchema,
    ResetTransactionReconciliationResponseSchema,
    VoidPaymentRequestResponseSchema,
    VoidTransactionResponseSchema,
    TerminalSaleResponseSchema,
    TerminalTokenizationRequestSchema,
    TerminalTokenizationResponseSchema,
    TerminalAbortResponseSchema,
    RecordTokenResponseSchema,
    ProcessCardTokenPaymentResponseSchema,
    ProcessCardNotPresentPaymentResponseSchema,
    ProcessAchPaymentResponseSchema,
    RecordReconciliationResponseSchema,
    RecordPaymentRequestReconciliationRequestSchema,
    RecordPaymentRequestReconciliationResponseSchema,
    CreateDraftPaymentRequestRequestSchema,
    CreateDraftPaymentRequestResponseSchema,
    PaymentRequestOwnerCandidatesResponseSchema,
    UploadPaymentRequestDocumentResponseSchema,
    FinalizeSendPaymentRequestRequestSchema,
    FinalizeSendPaymentRequestResponseSchema,
    MerchantPaymentRequestSearchRequestSchema,
    MerchantPaymentRequestListResponseSchema,
    CustomerPaymentMethodListRequestSchema,
    MerchantPaymentMethodTokenListRequestSchema,
    MerchantPaymentMethodTokenDetailsViewSchema,
    MerchantPaymentMethodTokenListResponseSchema,
    PaymentRequestDeliveryEventListSchema,
    PaymentRequestDeliveryEventListRequestSchema,
    PaymentRequestDetailsViewSchema,
    CustomerPaymentMethodListResponseSchema,
    CustomerPaymentRequestDetailsRequestSchema,
    MerchantPaymentRequestDetailsRequestSchema,
    CustomerPaymentMethodBootstrapRequestSchema,
    CustomerPaymentMethodBootstrapResponseSchema,
    CustomerPaymentMethodBinLookupRequestSchema,
    CreateCustomerPaymentMethodRequestSchema,
    CreateCustomerPaymentMethodResponseSchema,
    CustomerPortalDashboardRequestSchema,
    CustomerPortalDashboardResponseSchema,
    CustomerPaymentHistoryRequestSchema,
    CustomerPaymentHistoryResponseSchema,
    CustomerPaymentRequestSearchRequestSchema,
    CustomerPaymentRequestListResponseSchema,
    ResendPaymentRequestRequestSchema,
    UpdateCustomerPaymentMethodRequestSchema,
    UpdateCustomerPaymentMethodBodyWithScopeSchema,
    UpdateCustomerPaymentMethodResponseSchema,
    DeleteCustomerPaymentMethodRequestSchema,
    DeleteCustomerPaymentMethodResponseSchema,
    CancelAndReissuePaymentRequestRequestSchema,
    CancelAndReissuePaymentRequestResponseSchema,
    PublicPaymentLinkActionResponseSchema,
    PublicPaymentRequestViewSchema,
    GuestPaymentEntryContextViewSchema,
    AuthenticatedPaymentEntryContextViewSchema,
    GuestPaymentRequestDocumentAccessResponseSchema,
    GuestPaymentSuccessViewSchema,
    GuestBinLookupRequestSchema,
    GuestHostedCardTokenQuoteRequestSchema,
    HostedCardTokenQuoteRequestSchema,
    HostedCardTokenQuoteResponseSchema,
    GuestProcessCardTokenPaymentRequestSchema,
    AuthenticatedProcessCardTokenPaymentRequestSchema,
    GuestProcessStoredPaymentMethodPaymentRequestSchema,
    ProcessStoredPaymentMethodPaymentRequestSchema,
    SendgridEventWebhookPayloadSchema,
    TwilioStatusCallbackPayloadSchema,
} from './schemas/payment.schemas';
import {
    ProcessorSearchRequestSchema,
    ProcessorSearchPageResponseSchema,
    CreateProcessorRequestSchema,
    GetProcessorResponseSchema,
    UpdateProcessorRequestSchema,
    MerchantAccountSearchRequestSchema,
    MerchantAccountSearchPageResponseSchema,
    MerchantAccountUuidParamSchema,
    CreateMerchantAccountRequestSchema,
    UpdateMerchantAccountRequestSchema,
    GetMerchantAccountResponseSchema,
    LogicalTerminalSearchRequestSchema,
    LogicalTerminalSearchPageResponseSchema,
    LogicalTerminalUuidParamSchema,
    CreateLogicalTerminalRequestSchema,
    UpdateLogicalTerminalRequestSchema,
    GetLogicalTerminalResponseSchema,
    MerchantOrganizationSearchRequestSchema,
    MerchantOrganizationSearchPageResponseSchema,
    PreviewLogicalTerminalOrganizationChangeRequestSchema,
    PreviewLogicalTerminalOrganizationChangeResponseSchema,
    TerminalDeviceSearchRequestSchema,
    TerminalDeviceSearchPageResponseSchema,
    TerminalDeviceUuidParamSchema,
    CreateTerminalDeviceRequestSchema,
    UpdateTerminalDeviceRequestSchema,
    GetTerminalDeviceResponseSchema,
} from './schemas/payments-infrastructure.schemas';
import {
    CspViolationReportSchema,
    HealthResponseSchema,
    SambaLatestReleaseResponseSchema,
    SambaInstallerQaQuerySchema,
    SambaModuleListResponseSchema,
    SambaVersionResponseSchema,
} from './schemas/common.schemas';
import { WindowsArtifactQaSchema } from './schemas/windows-artifact-qa.schemas';
import {
    EnvironmentVariablesResponseSchema,
    AwsCredentialsResponseSchema,
    GetCallerIdentityResponseSchema,
    GetSystemInfoResponseSchema,
    GetSesDiagnosticsResponseSchema,
} from './schemas/diagnostics.schemas';
import {
    ListEncryptionKeyCredentialRotationAttemptsRequestSchema,
    ListEncryptionKeyCredentialRotationAttemptsResponseSchema,
    ListEncryptionKeyCredentialsRequestSchema,
    ListEncryptionKeyCredentialsResponseSchema,
    ListEncryptionKeysRequestSchema,
    ListEncryptionKeysResponseSchema,
    RekeyEncryptionKeyUsageRequestSchema,
    RekeyEncryptionKeyUsageResponseSchema,
    RotateEncryptionKeyResponseSchema,
} from './schemas/encryption-key.schemas';
import {
    ListSecretsResponseSchema,
    GetSecretResponseSchema,
    SecretMetadataResponseSchema,
    SecretWithCredentialsResponseSchema,
    CreateSecretRequestSchema,
    CreateSecretResponseSchema,
    CredentialTestRequestSchema,
    CredentialTestResultSchema,
    ListSecretsForOrganizationQuerySchema,
    CredentialUsagesResponseSchema,
    DisconnectCredentialUsagesRequestSchema,
    DisconnectCredentialUsagesResponseSchema,
} from './schemas/secret.schemas';
import type { ListSecretsForOrganizationQuery } from './secret.types';
import {
    AssignBundleRequestSchema,
    GrantPermissionRequestSchema,
    ListPublicBundlesResponseSchema,
    PermissionsBundlePublicSchema,
    RepairAuditedPermissionRequestSchema,
    SharedPermissionBundlesSearchPageResponseSchema,
    SharedPermissionBundlesSearchRequestSchema,
    RevokePermissionRequestSchema,
    RoleEligibilitySchema,
    RoleAssignmentPermissionsViewSchema,
    PermissionSourceRoleAssignmentsSearchRequestSchema,
    PermissionSourceRoleAssignmentsSearchPageResponseSchema,
    PolicyPermissionBundleRoleAssignmentsSearchRequestSchema,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
    OrganizationScopedPermissionSourceOptionsResponseSchema,
    PolicyPermissionBundlesSearchRequestSchema,
    PolicyPermissionBundlesSearchPageResponseSchema,
    ListPolicyPermissionGrantsResponseSchema,
    GrantPolicyPermissionBundleRequestSchema,
    ReorderPolicyPermissionGrantsRequestSchema,
} from './schemas/permission.schemas';
import {
    AllowedWebsiteTemplateBlastRadiusResponseSchema,
    AllowedWebsiteTemplateMutationResponseSchema,
    AllowedWebsiteTemplateSearchPageResponseSchema,
    AllowedWebsiteTemplateSearchRequestSchema,
    AllowedWebsiteTemplateUuidRequestSchema,
    CreateAllowedWebsiteTemplateRequestSchema,
    CreateLocalOrganizationAllowedWebsiteRequestSchema,
    DeleteOrganizationAllowedWebsiteResponseSchema,
    ListOrganizationAllowedWebsitesResponseSchema,
    OrganizationAllowedWebsiteMutationResponseSchema,
    PromoteOrganizationWebsiteToTemplateRequestSchema,
    PromoteOrganizationWebsiteToTemplateResponseSchema,
    ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema,
    UpdateAllowedWebsiteTemplateRequestSchema,
    UpdateOrganizationAllowedWebsiteRequestSchema,
    SafeDeleteAllowedWebsiteTemplateResponseSchema,
} from './schemas/allowed-website-template.schemas';
import type {
    SharedPermissionBundlesSearchPageResponse,
    SharedPermissionBundlesSearchRequest,
} from './permission.types';
import {
    CanCreateUserResponseSchema,
    CanModifyUserResponseSchema,
    CanDeleteUserResponseSchema,
    HasAccessToOrganizationResponseSchema,
    AccessibleOrganizationIdsResponseSchema,
    AccessibleUserIdsResponseSchema,
    AccessibleOrganizationsResponseSchema,
    AccessibleUsersResponseSchema,
    IsAuthorizedRequestSchema,
    IsAuthorizedResponseSchema,
    type IsAuthorizedRequest,
    type AccessibleOrganizationIdsRequest,
    type AccessibleUserIdsRequestNoPkey,
    HasAccessToUserResponseSchema,
    CanImpersonateRoleAssignmentResponseSchema,
} from './schemas/authorization.schemas';
import { MessageResponseSchema, VoidResponseSchema } from './schemas/common.schemas';
import {
    CloudLoggingQueryRequestSchema,
    CloudLoggingOptionSearchRequestSchema,
    ListCloudLoggingEntriesPageResponseSchema,
    ListCloudLoggingOptionsPageResponseSchema,
    LogIngestRequestSchema,
} from './schemas/logging.schemas';

/**
 * Check if Content-Type header indicates JSON
 */
function isJsonContentType(contentType: string | null): boolean {
    if (contentType === null) {
        return false;
    }
    const normalized = contentType.toLowerCase().trim();
    return normalized.startsWith('application/json') || normalized.startsWith('application/json;');
}

function parseErrorBody(responseBody: string, response: Response): unknown {
    if (!responseBody || responseBody.trim() === '') {
        return undefined;
    }
    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        return undefined;
    }
    try {
        return parseJsonWithNativeSafeNumbers(responseBody);
    } catch {
        return undefined;
    }
}

/**
 * Factory function to create PILLAR API client instances
 * @param onSessionExpired Optional callback for handling 401 responses
 * @param baseUrl Base URL for the API (defaults to '/api')
 * @param onTraceContext Optional callback for propagating trace context
 * @param onValidationError Optional callback for response validation failures
 * @returns Object containing all API client functions
 */
export function createPillarApi(
    onSessionExpired?: () => void,
    baseUrl: string = '/api',
    onTraceContext?: (traceHeaderValue: string) => void,
    onValidationError?: (error: ApiValidationError) => void
) {
    const options: PillarApiClientOptions = {
        onSessionExpired,
        baseUrl,
        enableIdempotencyHeaders: true,
        onTraceContext,
        onValidationError,
    };
    return createPillarApiWithOptions(options);
}

export function dedupe<ArgumentsTuple extends readonly unknown[], ReturnValue>(
    functionToDedupe: (...args: ArgumentsTuple) => Promise<ReturnValue>
): (...args: ArgumentsTuple) => Promise<ReturnValue> {
    const cacheBySerializedArguments = new Map<string, Promise<ReturnValue>>();

    return async (...args: ArgumentsTuple): Promise<ReturnValue> => {
        const serializedArgumentsKey = stringifyLosslessJson(args);
        const existingPromise = cacheBySerializedArguments.get(serializedArgumentsKey);
        if (existingPromise !== undefined) {
            return existingPromise;
        }

        const dedupedPromise = functionToDedupe(...args);
        cacheBySerializedArguments.set(serializedArgumentsKey, dedupedPromise);
        return await dedupedPromise.finally(() => {
            cacheBySerializedArguments.delete(serializedArgumentsKey);
        });
    };
}

export const IpxCredentialManagementUuidSchema: z.ZodType<string> = z.string().uuid();

function buildIpxCredentialManagementOrganizationPoliciesEndpoint(
    data: ListOrganizationIpxCredentialPoliciesRequest
): string {
    const validatedData: ListOrganizationIpxCredentialPoliciesRequest =
        ListOrganizationIpxCredentialPoliciesRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    if (validatedData.limit !== undefined) {
        queryParams.set('limit', String(validatedData.limit));
    }
    if (validatedData.cursor !== undefined) {
        queryParams.set('cursor', validatedData.cursor);
    }
    const encodedOrganizationUuid: string = encodeURIComponent(validatedData.organization_uuid);
    const queryString: string = queryParams.toString();
    const endpoint = `/portal/v1/ipx-credential-management/organizations/${encodedOrganizationUuid}/policies`;
    return queryString.length === 0 ? endpoint : `${endpoint}?${queryString}`;
}

function buildIpxCredentialManagementDashboardWebsiteCredentialsEndpoint(
    data: ListDashboardWebsiteCredentialsRequest
): string {
    const validatedData: ListDashboardWebsiteCredentialsRequest =
        ListDashboardWebsiteCredentialsRequestSchema.parse(data);
    const encodedOrganizationUuid: string = encodeURIComponent(validatedData.organization_uuid);
    return `/portal/v1/ipx-credential-management/organizations/${encodedOrganizationUuid}/dashboard-website-credentials`;
}

function buildIpxCredentialManagementDashboardWebsiteCredentialEndpoint(
    data: Pick<
        CreateDashboardWebsiteCredentialRequest,
        'organization_uuid' | 'organization_ipx_credential_policy_uuid'
    >
): string {
    const validatedOrganizationUuid = z.string().uuid().parse(data.organization_uuid);
    const validatedPolicyUuid = z
        .string()
        .uuid()
        .parse(data.organization_ipx_credential_policy_uuid);
    return `/portal/v1/ipx-credential-management/organizations/${encodeURIComponent(
        validatedOrganizationUuid
    )}/dashboard-website-credentials/${encodeURIComponent(validatedPolicyUuid)}`;
}

function buildIpxCredentialManagementPolicyEndpoint(
    policyUuid: string,
    suffix: string = ''
): string {
    const validatedPolicyUuid: string = IpxCredentialManagementUuidSchema.parse(policyUuid);
    return `/portal/v1/ipx-credential-management/policies/${encodeURIComponent(
        validatedPolicyUuid
    )}${suffix}`;
}

function buildIpxCredentialManagementCredentialEndpoint(
    credentialUuid: string,
    suffix: string = ''
): string {
    const validatedCredentialUuid: string = IpxCredentialManagementUuidSchema.parse(credentialUuid);
    return `/portal/v1/ipx-credential-management/credentials/${encodeURIComponent(
        validatedCredentialUuid
    )}${suffix}`;
}

function buildIpxCredentialManagementCredentialRotationHistoryEndpoint(
    data: CredentialRotationHistoryRequest
): string {
    const validatedData: CredentialRotationHistoryRequest =
        CredentialRotationHistoryRequestSchema.parse(data);
    const queryParams = new URLSearchParams();
    if (validatedData.limit !== undefined) {
        queryParams.set('limit', String(validatedData.limit));
    }
    if (validatedData.cursor !== undefined) {
        queryParams.set('cursor', validatedData.cursor);
    }
    if (validatedData.state !== undefined) {
        queryParams.set('state', validatedData.state);
    }
    const queryString = queryParams.toString();
    const endpoint = buildIpxCredentialManagementCredentialEndpoint(
        validatedData.credential_uuid,
        '/rotation-attempts'
    );
    return queryString.length === 0 ? endpoint : `${endpoint}?${queryString}`;
}

function buildRpaBuilderDefinitionEndpoint(definitionUuid: string, suffix: string = ''): string {
    const validatedDefinitionUuid: string = z.string().uuid().parse(definitionUuid);
    return `/portal/v1/rpa-builder/credential-definitions/${encodeURIComponent(
        validatedDefinitionUuid
    )}${suffix}`;
}

function buildRpaBuilderListCredentialDefinitionsEndpoint(
    data: RpaBuilderListCredentialDefinitionsRequest
): string {
    const validatedData: RpaBuilderListCredentialDefinitionsRequest =
        RpaBuilderListCredentialDefinitionsRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.system_id !== undefined) {
        queryParams.set('system_id', String(validatedData.system_id));
    }
    if (validatedData.credential_definition_key !== undefined) {
        queryParams.set('credential_definition_key', validatedData.credential_definition_key);
    }
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    const queryString = queryParams.toString();
    return queryString.length === 0
        ? '/portal/v1/rpa-builder/credential-definitions'
        : `/portal/v1/rpa-builder/credential-definitions?${queryString}`;
}

function buildRpaBuilderWebResourceModelEndpoint(modelUuid: string): string {
    const validatedModelUuid: string = z.string().uuid().parse(modelUuid);
    return `/portal/v1/rpa-builder/web-resource-models/${encodeURIComponent(validatedModelUuid)}`;
}

function buildRpaBuilderListWebResourceModelsEndpoint(
    data: RpaBuilderListWebResourceModelsRequest
): string {
    const validatedData: RpaBuilderListWebResourceModelsRequest =
        RpaBuilderListWebResourceModelsRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.system_id !== undefined) {
        queryParams.set('system_id', String(validatedData.system_id));
    }
    if (validatedData.initial_navigation_url !== undefined) {
        queryParams.set('initial_navigation_url', validatedData.initial_navigation_url);
    }
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    const queryString = queryParams.toString();
    return queryString === ''
        ? '/portal/v1/rpa-builder/web-resource-models'
        : `/portal/v1/rpa-builder/web-resource-models?${queryString}`;
}

function buildAllowedWebsiteTemplateEndpoint(templateUuid: string, suffix: string = ''): string {
    const validatedTemplateUuid: string = z.string().uuid().parse(templateUuid);
    return `/portal/v1/allowed_website_templates/${encodeURIComponent(
        validatedTemplateUuid
    )}${suffix}`;
}

function buildOrganizationAllowedWebsitesEndpoint(
    organizationUuid: string,
    suffix: string = ''
): string {
    const validatedOrganizationUuid: string = z.string().uuid().parse(organizationUuid);
    return `/portal/v1/organization/${encodeURIComponent(
        validatedOrganizationUuid
    )}/allowed_websites${suffix}`;
}

function buildOrganizationAllowedWebsiteEndpoint(
    organizationUuid: string,
    organizationAllowedWebsiteUuid: string,
    suffix: string = ''
): string {
    const validatedOrganizationWebsiteUuid: string = z
        .string()
        .uuid()
        .parse(organizationAllowedWebsiteUuid);
    return buildOrganizationAllowedWebsitesEndpoint(
        organizationUuid,
        `/${encodeURIComponent(validatedOrganizationWebsiteUuid)}${suffix}`
    );
}

/**
 * Factory function to create PILLAR API client instances with custom fetch/headers.
 */
export function createPillarApiWithOptions(options: PillarApiClientOptions) {
    const schedulePillarApiRequest = createPillarApiRequestScheduler(options.maxConcurrentRequests);
    const baseUrl: string = options.baseUrl ?? '/api';
    const onSessionExpired: (() => void) | undefined = options.onSessionExpired;
    const fetchImplementation: FetchImplementation =
        options.fetchImplementation ??
        ((...args: FetchArguments) => {
            return globalThis.fetch(...args);
        });
    const getRequestHeaders: (() => HttpRequestHeaders) | undefined = options.getRequestHeaders;
    const enableIdempotencyHeaders: boolean = options.enableIdempotencyHeaders ?? false;
    const onResponse: ((response: Response) => void) | undefined = options.onResponse;
    const onTraceContext: ((traceHeaderValue: string) => void) | undefined = options.onTraceContext;
    const onValidationError: ((error: ApiValidationError) => void) | undefined =
        options.onValidationError;
    const traceHeaderSchema: z.ZodType<string> = z.string().min(1);
    const httpPermanentRedirects: Record<string, URL> = {};

    function resolveBaseUrlForRequest(): string {
        let baseUrlForResolution: string = baseUrl;
        if (baseUrlForResolution.startsWith('/')) {
            // Note: api-types is Node-targeted, so `globalThis.location` is not present in typings.
            // We still support relative baseUrls ("/api") in browser-like runtimes by reading location.origin.
            // eslint-disable-next-line no-restricted-syntax
            const locationValue: unknown = (globalThis as unknown as Record<string, unknown>)
                .location;
            const locationParseResult = z
                .object({ origin: z.string().min(1) })
                .passthrough()
                .safeParse(locationValue);
            if (!locationParseResult.success) {
                throw new Error(
                    `Invalid baseUrl "${baseUrlForResolution}": relative baseUrl requires globalThis.location.origin`
                );
            }
            baseUrlForResolution = `${locationParseResult.data.origin}${baseUrlForResolution}`;
        } else if (
            !baseUrlForResolution.startsWith('https://') &&
            !baseUrlForResolution.startsWith('http://')
        ) {
            throw new Error(
                `Invalid baseUrl "${baseUrlForResolution}": must start with "https://", "http://", or "/"`
            );
        }

        return baseUrlForResolution;
    }

    function buildRequestUrl(endpoint: string): URL {
        const baseUrlForResolution: string = resolveBaseUrlForRequest();
        const fullPath: string = endpoint.startsWith('/')
            ? `${baseUrlForResolution}${endpoint}`
            : `${baseUrlForResolution}/${endpoint}`;
        return new URL(fullPath);
    }

    // Make a type-safe API request with zod validation
    async function executePillarApiRequest<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: PillarApiRequestOptions = {}
    ): Promise<T> {
        const defaultHeaders: HttpRequestHeaders = getRequestHeaders ? getRequestHeaders() : {};
        const {
            method = 'GET',
            body,
            headers = {},
            handle401AsSessionExpired = true,
            acceptedStatusCodes = [],
            idempotencyKey,
            keepalive = false,
            preserveLosslessNumbers = false,
        } = options;
        const normalizedMethod = method.toUpperCase();
        const locationHeaderSchema: z.ZodType<string> = z.string().min(1);
        const isMultipartFormDataBody = isFormDataBody(body);
        const requestHeaders: Record<string, string> = {
            Accept: 'application/json',
            ...defaultHeaders,
            ...headers,
        };
        if (
            enableIdempotencyHeaders &&
            isMutatingHttpMethod(normalizedMethod) &&
            idempotencyKey !== null
        ) {
            requestHeaders['Idempotency-Key'] = idempotencyKey ?? generateIdempotencyKey();
        }
        if (!isMultipartFormDataBody) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        const requestInit: FetchRequestInit = {
            signal: options.signal,
            method: normalizedMethod,
            headers: requestHeaders,
            body:
                body === undefined
                    ? undefined
                    : isMultipartFormDataBody
                      ? body
                      : stringifyLosslessJson(body),
            credentials: 'include', // Important for session cookies
            keepalive,
        };

        const requestUrlAsURL: URL = buildRequestUrl(endpoint);
        const requestUrl: string = requestUrlAsURL.toString();
        let currentUrlAsURL: URL = httpPermanentRedirects[requestUrl] ?? requestUrlAsURL;
        let response: Response | null = null;
        const maxRedirects = 3;
        let redirectIsTransient: boolean = false;
        try {
            for (let redirectCount = 0; redirectCount <= maxRedirects; redirectCount += 1) {
                response = await fetchImplementation(currentUrlAsURL, requestInit);
                if (onResponse) {
                    onResponse(response);
                }
                if (response.status === 304) {
                    await response.text();
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Unsupported 304 response: API client has no cached response body'
                    );
                }
                if (response.status >= 300 && response.status < 400) {
                    // Consume redirect response body to avoid leaving the stream open
                    await response.text();
                    const locationHeaderValue: string | null = response.headers.get('location');
                    if (locationHeaderValue === null) {
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            'Redirect response missing Location header'
                        );
                    }
                    const locationParseResult = locationHeaderSchema.safeParse(locationHeaderValue);
                    if (!locationParseResult.success) {
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            'Redirect response has invalid Location header'
                        );
                    }
                    // Resolve Location header relative to the current request URL (RFC 7231)
                    // Location can be absolute (http://example.com/path) or relative (/path, ../path)
                    // If relative, resolve against the request URL that generated the redirect
                    currentUrlAsURL = new URL(locationParseResult.data, currentUrlAsURL);
                    redirectIsTransient =
                        redirectIsTransient || ![301, 308].includes(response.status);
                    if (!redirectIsTransient) {
                        httpPermanentRedirects[requestUrl] = currentUrlAsURL;
                    }
                    continue;
                }
                if (response.status >= 400) {
                    // The redirection might be stale and should be forgotten
                    delete httpPermanentRedirects[requestUrl];
                }
                break;
            }
            if (response === null) {
                throw new ApiError(500, 'Invalid Response', 'No response received from server');
            }

            const traceHeaderValue: string | null = response.headers.get('x-cloud-trace-context');
            if (traceHeaderValue !== null) {
                const traceHeaderParseResult: SafeParseResult<string> =
                    traceHeaderSchema.safeParse(traceHeaderValue);
                if (!traceHeaderParseResult.success) {
                    const validationError: ApiTraceHeaderValidationError = {
                        kind: 'trace-header',
                        headerValue: traceHeaderValue,
                        issues: traceHeaderParseResult.error.issues,
                    };
                    if (onValidationError) {
                        onValidationError(validationError);
                    }
                    throw new ApiError(
                        500,
                        'Invalid Response',
                        'Invalid X-Cloud-Trace-Context header'
                    );
                }
                if (onTraceContext) {
                    onTraceContext(traceHeaderParseResult.data);
                }
            }

            const responseBody = await response.text();

            // Handle 401 Unauthorized (session expired)
            if (response.status === 401) {
                if (onSessionExpired && handle401AsSessionExpired) {
                    onSessionExpired();
                }
                // Try to extract error message from response body
                const errorBody: unknown = parseErrorBody(responseBody, response);
                const errorMessage = extractErrorMessage(responseBody, response, response.status);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    errorMessage,
                    errorBody,
                    responseBody
                );
            }

            // Handle error responses - parse error message from payload
            if (!response.ok && !acceptedStatusCodes.includes(response.status)) {
                const errorBody: unknown = parseErrorBody(responseBody, response);
                const errorMessage = extractErrorMessage(responseBody, response, response.status);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    errorMessage,
                    errorBody,
                    responseBody
                );
            }

            // Handle 204 No Content
            if (response.status === 204) {
                return schema.parse(undefined);
            }

            // Parse and validate JSON only if Content-Type indicates JSON
            if (!responseBody || responseBody.trim() === '') {
                throw new ApiError(response.status, response.statusText, 'Empty response body');
            }

            const contentType: string | null = response.headers.get('content-type');
            if (!isJsonContentType(contentType)) {
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Expected JSON response but got Content-Type: ${contentType ?? 'missing'}`
                );
            }

            let parsed: unknown;
            try {
                parsed = preserveLosslessNumbers
                    ? parseLosslessJson(responseBody)
                    : parseJsonWithNativeSafeNumbers(responseBody);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Failed to parse JSON response: ${errorMessage}`
                );
            }

            // Always validate against schema (required)
            try {
                return schema.parse(parsed);
            } catch (validationError: unknown) {
                if (validationError instanceof z.ZodError) {
                    const validationErrorDetails: ApiResponseValidationError = {
                        kind: 'response-body',
                        endpoint,
                        issues: validationError.issues,
                        response: parsed,
                    };
                    if (onValidationError) {
                        onValidationError(validationErrorDetails);
                    }
                    throw new ApiError(
                        500,
                        'Invalid Response',
                        `API response validation failed: ${validationError.issues.map((issue: z.core.$ZodIssue) => issue.message).join(', ')}`
                    );
                }
                throw validationError;
            }
        } finally {
            await cancelUnreadPillarApiResponse(response);
        }
    }

    function fetchPillarAPI<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: PillarApiRequestOptions = {}
    ): Promise<T> {
        return schedulePillarApiRequest(
            () => executePillarApiRequest(endpoint, schema, options),
            options.signal
        );
    }

    const requestWithSchema = async <T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options?: PillarApiRequestOptions
    ): Promise<T> => {
        const requestOptions = options ?? {};
        return fetchPillarAPI(endpoint, schema, requestOptions);
    };

    const requestTextWithSchema = async (
        endpoint: string,
        schema: z.ZodSchema<string>,
        options?: {
            method?: string;
            headers?: HttpRequestHeaders;
            handle401AsSessionExpired?: boolean;
        }
    ): Promise<string> =>
        schedulePillarApiRequest(async () => {
            const requestOptions = options ?? {};
            const url: string = buildRequestUrl(endpoint).toString();
            const defaultHeaders: HttpRequestHeaders =
                getRequestHeaders !== undefined ? getRequestHeaders() : {};
            const headers: HttpRequestHeaders = {
                ...defaultHeaders,
                ...(requestOptions.headers ?? {}),
            };
            const method: string = requestOptions.method ?? 'GET';

            const response = await fetchImplementation(url, {
                method,
                credentials: 'include',
                headers,
            });

            try {
                if (response.status === 401 && requestOptions.handle401AsSessionExpired !== false) {
                    onSessionExpired?.();
                }

                if (!response.ok) {
                    const errorText: string = await response.text();
                    const errorMessage: string =
                        errorText.trim().length > 0 ? errorText : response.statusText;
                    throw new ApiError(response.status, response.statusText, errorMessage);
                }

                const responseBody: string = await response.text();
                if (responseBody.trim().length === 0) {
                    throw new ApiError(response.status, response.statusText, 'Empty response body');
                }

                return schema.parse(responseBody);
            } finally {
                await cancelUnreadPillarApiResponse(response);
            }
        });

    const authApi = {
        redeemFirstLoginInvitation: (data: FirstLoginRequest) =>
            fetchPillarAPI<FirstLoginResponse>(
                '/portal/v1/user_management/any_user/first_login',
                FirstLoginResponseSchema,
                {
                    method: 'POST',
                    body: FirstLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        login: (data: LoginRequest) =>
            fetchPillarAPI('/portal/v1/user_management/any_user/login', LoginResponseSchema, {
                method: 'POST',
                body: data,
                handle401AsSessionExpired: false,
            }),

        requestPasswordlessLogin: (data: PasswordlessLoginRequest) =>
            fetchPillarAPI<PasswordlessLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/passwordless/request',
                PasswordlessLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        verifyPasswordlessLoginCode: (data: PasswordlessLoginCodeVerificationRequest) =>
            fetchPillarAPI(
                '/portal/v1/user_management/any_user/passwordless/verify-code',
                LoginResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginCodeVerificationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        exchangePasswordlessLoginLink: (data: PasswordlessLoginLinkExchangeRequest) =>
            fetchPillarAPI(
                '/portal/v1/user_management/any_user/passwordless/exchange-link',
                LoginResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginLinkExchangeRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        completeCurrentCustomerPaymentLinkAttachment: (
            data: CompleteCustomerPaymentLinkAttachmentRequest
        ) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/complete_customer_payment_link_attachment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: CompleteCustomerPaymentLinkAttachmentRequestSchema.parse(data),
                }
            ),

        checkProposedPassword: (data: CheckProposedPasswordRequest) =>
            fetchPillarAPI<CheckProposedPasswordResponse>(
                '/portal/v1/user_management/any_user/check_proposed_password',
                CheckProposedPasswordResponseSchema,
                {
                    method: 'POST',
                    body: CheckProposedPasswordRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        verifyTotp: (data: VerifyTotpRequest) =>
            fetchPillarAPI(
                '/portal/v1/user_management/any_user/verify_totp',
                VerifyTotpResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestEmailMfaLogin: () =>
            fetchPillarAPI<MfaLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/request_email_mfa_login',
                MfaLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyEmailMfaLogin: (data: VerifyMfaLoginRequest) =>
            fetchPillarAPI<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/verify_email_mfa_login',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        requestSmsMfaLogin: () =>
            fetchPillarAPI<MfaLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/request_sms_mfa_login',
                MfaLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifySmsMfaLogin: (data: VerifyMfaLoginRequest) =>
            fetchPillarAPI<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/verify_sms_mfa_login',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getMfaSetupStatus: () =>
            fetchPillarAPI<MfaSetupStatusResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/status',
                MfaSetupStatusResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        setupLoginMfaTotp: () =>
            fetchPillarAPI<SetupTotpResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/totp/setup',
                SetupTotpResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaTotpSetup: (data: EnableTotpRequest) =>
            fetchPillarAPI<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/totp/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestLoginMfaEmailSetup: () =>
            fetchPillarAPI<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/email/request',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaEmailSetup: (data: VerifyMfaActivationRequest) =>
            fetchPillarAPI<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/email/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        requestLoginMfaSmsSetup: () =>
            fetchPillarAPI<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/sms/request',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaSmsSetup: (data: VerifyMfaActivationRequest) =>
            fetchPillarAPI<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/sms/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getMfaStatus: dedupe(() =>
            fetchPillarAPI<MfaPolicyStatusResponse>(
                '/portal/v1/user_management/any_user/mfa_status',
                MfaPolicyStatusResponseSchema
            )
        ),

        requestEmailMfaActivation: () =>
            fetchPillarAPI<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/request_email_mfa_activation',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                }
            ),

        verifyEmailMfaActivation: (data: VerifyMfaActivationRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_email_mfa_activation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                }
            ),

        requestSmsMfaActivation: () =>
            fetchPillarAPI<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/request_sms_mfa_activation',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                }
            ),

        verifySmsMfaActivation: (data: VerifyMfaActivationRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_sms_mfa_activation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                }
            ),

        disableMfa: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        disableEmailMfa: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_email_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        disableSmsMfa: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_sms_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        logout: () =>
            fetchPillarAPI('/portal/v1/user_management/any_user/logout', MessageResponseSchema, {
                method: 'POST',
            }),

        swapCookieForToken: () =>
            fetchPillarAPI<SwapCookieForTokenResponse>(
                '/portal/v1/user_management/any_user/swap_cookie_for_token',
                SwapCookieForTokenResponseSchema,
                {
                    method: 'POST',
                }
            ),

        issueNativeAppLoginLink: () =>
            fetchPillarAPI<NativeAppLoginLinkResponse>(
                '/portal/v1/user_management/any_user/issue_native_app_login_link',
                NativeAppLoginLinkResponseSchema,
                {
                    method: 'POST',
                }
            ),

        cancelNativeAppLoginLink: (
            data: CancelNativeAppLoginLinkRequest,
            options: Pick<PillarApiRequestOptions, 'keepalive'> = {}
        ) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/cancel_native_app_login_link',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: CancelNativeAppLoginLinkRequestSchema.parse(data),
                    keepalive: options.keepalive,
                }
            ),

        swapTokenForCookie: (data: SwapTokenForCookieRequest) =>
            fetchPillarAPI<SwapTokenForCookieResponse>(
                '/portal/v1/user_management/any_user/swap_token_for_cookie',
                SwapTokenForCookieResponseSchema,
                {
                    method: 'POST',
                    body: SwapTokenForCookieRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getProfile: dedupe((options: GetProfileRequestOptions = {}) =>
            fetchPillarAPI<SessionUserProfileDetailedResponse>(
                '/portal/v1/user_management/any_user/profile',
                SessionUserProfileDetailedResponseSchema,
                options
            )
        ),

        updateProfile: (data: UpdateProfileRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/profile',
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: UpdateProfileRequestSchema.parse(data),
                }
            ),

        getActiveRoleAssignment: dedupe(() =>
            fetchPillarAPI<ActiveRoleAssignmentResponse | null>(
                '/portal/v1/user_management/any_user/active_role_assignment',
                ActiveRoleAssignmentResponseSchema.nullable()
            )
        ),

        requestSelfVerificationEmail: dedupe(() =>
            fetchPillarAPI<SendVerificationEmailResponse>(
                '/portal/v1/user_management/any_user/request_self_verification_email',
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        verifyEmail: (token: string, password?: string) =>
            fetchPillarAPI<VerifyEmailResponse>(
                '/portal/v1/user_management/any_user/verify_email',
                VerifyEmailResponseSchema,
                {
                    method: 'POST',
                    body: {
                        token,
                        ...(password !== undefined ? { password } : {}),
                    } satisfies VerifyEmailRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        createCustomerSelfOnboarding: (data: CreateCustomerSelfOnboardingRequest) =>
            fetchPillarAPI<CreateCustomerSelfOnboardingResponse>(
                '/portal/v1/user_management/any_user/customer_self_onboarding',
                CreateCustomerSelfOnboardingResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestPasswordReset: dedupe((email: string) =>
            fetchPillarAPI<RequestPasswordResetResponse>(
                '/portal/v1/user_management/any_user/request_password_reset',
                RequestPasswordResetResponseSchema,
                {
                    method: 'POST',
                    body: { email } satisfies RequestPasswordResetRequest,
                }
            )
        ),

        validatePasswordResetToken: (token: string) =>
            fetchPillarAPI<ValidatePasswordResetTokenResponse>(
                '/portal/v1/user_management/any_user/validate_reset_password_token',
                ValidatePasswordResetTokenResponseSchema,
                {
                    method: 'POST',
                    body: { token } satisfies ValidatePasswordResetTokenRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        resetPassword: (token: string, newPassword: string) =>
            fetchPillarAPI<ResetPasswordResponse>(
                '/portal/v1/user_management/any_user/reset_password',
                ResetPasswordResponseSchema,
                {
                    method: 'POST',
                    body: { token, newPassword } satisfies ResetPasswordRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        setDefaultRoleAssignment: (role_assignment_uuid: string) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_default_role_assignment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetDefaultRoleAssignmentUuidRequest,
                }
            ),

        listSelectableRoleAssignments: () =>
            fetchPillarAPI<SelectableRoleAssignmentsResponse>(
                '/portal/v1/user_management/any_user/selectable_role_assignments',
                SelectableRoleAssignmentsResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        setActiveRoleAssignment: (role_assignment_uuid: string) =>
            fetchPillarAPI<SetActiveRoleAssignmentResponse>(
                '/portal/v1/user_management/any_user/set_active_role_assignment',
                SetActiveRoleAssignmentResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetActiveRoleAssignmentUuidRequest,
                }
            ),

        startImpersonation: (role_assignment_uuid: string) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/start_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: StartImpersonationRequestSchema.parse({
                        role_assignment_uuid,
                    }) satisfies StartImpersonationRequest,
                }
            ),

        stopImpersonation: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/stop_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        getImpersonationContext: dedupe(() =>
            fetchPillarAPI<ImpersonationContextResponse | null>(
                '/portal/v1/user_management/any_user/impersonation_context',
                ImpersonationContextResponseSchema.nullable()
            )
        ),

        changePassword: (data: ChangePasswordRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/change_password',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setEmails: (data: SetUserEmailsRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_emails',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setPhones: (data: SetUserPhonesRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/set_phones',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserPhonesRequestSchema.parse(data),
                }
            ),

        requestAddEmailVerification: (data: RequestAddEmailVerificationRequest) =>
            fetchPillarAPI<RequestAddEmailVerificationResponse>(
                '/portal/v1/user_management/any_user/request_add_email_verification',
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        requestAddPhoneVerification: (data: RequestAddPhoneVerificationRequest) =>
            fetchPillarAPI<RequestAddPhoneVerificationResponse>(
                '/portal/v1/user_management/any_user/request_add_phone_verification',
                RequestAddPhoneVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddPhoneVerificationRequestSchema.parse(data),
                }
            ),

        verifyAddPhone: (data: VerifyAddPhoneRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_add_phone',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyAddPhoneRequestSchema.parse(data),
                }
            ),

        setupTOTP: dedupe(() =>
            fetchPillarAPI<SetupTotpResponse>(
                '/portal/v1/user_management/any_user/setup_totp',
                SetupTotpResponseSchema
            )
        ),

        enableTOTP: (data: EnableTotpRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/enable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        disableTOTP: () =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        generateBackupCodes: dedupe(() =>
            fetchPillarAPI<GenerateBackupCodesResponse>(
                '/portal/v1/user_management/any_user/generate_backup_codes',
                GenerateBackupCodesResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getSessions: () =>
            fetchPillarAPI<GetSessionsResponse>(
                '/portal/v1/user_management/any_user/sessions',
                GetSessionsResponseSchema
            ),

        expireSession: (data: ExpireSessionUuidRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/any_user/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireSessionUuidRequestSchema.parse(data),
                }
            ),

        getAllSessions: () =>
            fetchPillarAPI<GetSessionsResponse>(
                '/portal/v1/user_management/admin/sessions',
                GetSessionsResponseSchema
            ),

        listAllSessionsPage: (params: SessionsSearchRequest = {}) => {
            const requestBody: SessionsSearchRequest = SessionsSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListSessionsPageResponse>(
                '/portal/v1/user_management/admin/sessions/search',
                ListSessionsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        expireSessionAdmin: (data: ExpireUserSessionRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/admin/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireUserSessionRequestSchema.parse(data),
                }
            ),

        setSessionConsoleAdmin: (data: UpdateSessionConsoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/admin/set_session_console',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: UpdateSessionConsoleRequestSchema.parse(data),
                }
            ),

        deleteMyAccount: (password: string) =>
            fetchPillarAPI<DeleteMyAccountResponse>(
                '/portal/v1/user_management/any_user',
                DeleteMyAccountResponseSchema,
                {
                    method: 'DELETE',
                    body: { password } satisfies DeleteMyAccountRequest,
                    handle401AsSessionExpired: false,
                }
            ),
    };

    const userApi = {
        listSingleUseTokensAdmin: (request: SingleUseTokensSearchRequest) =>
            fetchPillarAPI<ListSingleUseTokensResponse>(
                '/portal/v1/user_management/pillar_admin/single_use_tokens/search',
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        listSingleUseTokensByUserAdmin: (userUuid: string, request: SingleUseTokensSearchRequest) =>
            fetchPillarAPI<ListSingleUseTokensResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/single_use_tokens/search`,
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        expireSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/expire`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        resendSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/resend`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        setUserEmailsAdmin: (userUuid: string, data: SetUserEmailsRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/set_emails`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setUserPhonesAdmin: (userUuid: string, data: SetUserPhonesRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/set_phones`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserPhonesRequestSchema.parse(data),
                }
            ),

        requestAddEmailVerificationAdmin: (
            userUuid: string,
            data: RequestAddEmailVerificationRequest
        ) =>
            fetchPillarAPI<RequestAddEmailVerificationResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_add_email_verification`,
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        requestAddPhoneVerificationAdmin: (
            userUuid: string,
            data: RequestAddPhoneVerificationRequest
        ) =>
            fetchPillarAPI<RequestAddPhoneVerificationResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_add_phone_verification`,
                RequestAddPhoneVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddPhoneVerificationRequestSchema.parse(data),
                }
            ),

        verifyAddPhoneAdmin: (userUuid: string, data: VerifyAddPhoneRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/verify_add_phone`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyAddPhoneRequestSchema.parse(data),
                }
            ),

        resetPasswordAdmin: dedupe((userUuid: string, newPassword: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/reset_password`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ResetPasswordAdminRequestSchema.parse({
                        new_password: newPassword,
                    } satisfies ResetPasswordAdminRequest),
                }
            )
        ),

        requestPasswordResetAdmin: (userUuid: string, data: RequestPasswordResetAdminRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_password_reset`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: RequestPasswordResetAdminRequestSchema.parse(data),
                }
            ),

        resetMfaAdmin: (userUuid: string, data: MfaResetRequest) =>
            fetchPillarAPI<MfaResetResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/reset_mfa`,
                MfaResetResponseSchema,
                {
                    method: 'POST',
                    body: MfaResetRequestSchema.parse(data),
                }
            ),

        resetEmailAuthenticationAttemptLimit: (data: EmailAuthenticationAttemptLimitResetRequest) =>
            fetchPillarAPI<void>(
                '/portal/v1/user_management/pillar_admin/email_authentication_attempt_limit/reset',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: EmailAuthenticationAttemptLimitResetRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            ),

        setMfaRequiredAdmin: (userUuid: string, data: SetUserMfaRequiredRequest) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/mfa_required`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserMfaRequiredRequestSchema.parse(data),
                }
            ),

        sendVerificationEmailAdmin: dedupe((userUuid: string) =>
            fetchPillarAPI<SendVerificationEmailResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/send_verification_email`,
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getUser: (userUuid: string) =>
            fetchPillarAPI<UserProfileDetailedPublicResponse>(
                `/portal/v2/user_management/user/${userUuid}`,
                UserProfileDetailedPublicResponseV2Schema
            ),

        provisionOrganizationalUser: dedupe((data: ProvisionOrganizationalUserRequest) =>
            fetchPillarAPI<ProvisionedUser>(
                '/portal/v2/user_management/pillar_admin/create_ouser',
                ProvisionedUserV2Schema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        provisionInternalUser: dedupe((data: ProvisionUserRequest) =>
            fetchPillarAPI<ProvisionedUser>(
                '/portal/v2/user_management/superadmin/create_iuser',
                ProvisionedUserV2Schema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        resendLoginInvitationAdmin: (userUuid: string) =>
            fetchPillarAPI<LoginInvitationAdministrationState>(
                `/portal/v2/user_management/pillar_admin/user/${userUuid}/login-invitation/resend`,
                LoginInvitationAdministrationStateV2Schema,
                { method: 'POST' }
            ),

        revokeLoginInvitationAdmin: (userUuid: string) =>
            fetchPillarAPI<LoginInvitationAdministrationState>(
                `/portal/v2/user_management/pillar_admin/user/${userUuid}/login-invitation/revoke`,
                LoginInvitationAdministrationStateV2Schema,
                { method: 'POST' }
            ),

        resendAllLoginInvitationsAdmin: (data: ResendAllLoginInvitationsRequest) =>
            fetchPillarAPI<ResendAllLoginInvitationsPage>(
                '/portal/v2/user_management/pillar_admin/login-invitations/resend-all',
                ResendAllLoginInvitationsPageV2Schema,
                {
                    method: 'POST',
                    body: ResendAllLoginInvitationsRequestSchema.parse(data),
                }
            ),

        updateUser: dedupe((userUuid: string, data: UpdateUserRequest) =>
            fetchPillarAPI<UpdateUserResponse>(
                `/portal/v1/user_management/pillar_admin/update_user/${userUuid}`,
                UpdateUserResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteUser: (userUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/superadmin/delete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteUser: (userUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/superadmin/undelete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        assignRole: (data: AssignRoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/assign_role',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        revokeRole: (data: RevokeRoleRequest) =>
            fetchPillarAPI<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/revoke_role',
                MessageResponseSchema,
                {
                    method: 'DELETE',
                    body: data,
                }
            ),

        updateRoleAssignment: (
            roleAssignmentUuid: string,
            data: Omit<UpdateRoleAssignmentRequest, 'role_assignment_uuid'>
        ) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/role_assignments/${roleAssignmentUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        setDefaultRoleAssignment: (userUuid: string, roleAssignmentUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/set_default_role_assignment/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: {
                        role_assignment_uuid: roleAssignmentUuid,
                    } satisfies SetDefaultRoleAssignmentRequest,
                }
            ),

        searchUsers: (params: UserOmnisearchPageRequest) => {
            const queryParams = new URLSearchParams();
            if (params.q !== undefined) {
                queryParams.set('q', params.q);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            if (params.organization_uuid !== undefined) {
                queryParams.set('organization_uuid', params.organization_uuid);
            }
            return fetchPillarAPI<UserOmnisearchPageResponse>(
                `/portal/v1/user_management/users/search?${queryParams.toString()}`,
                UserOmnisearchPageResponseSchema
            );
        },

        listInternalUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/internal_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listIsoUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/iso_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listMerchantUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/merchant_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listCustomerUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            fetchPillarAPI<PublicUserListingPageResponse>(
                '/portal/v1/customer_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),
    };

    const organizationApi = {
        resolveBusinessTimeZone: (
            organizationUuid: string,
            request: ResolveBusinessTimeZoneRequest
        ) => {
            const resolveBusinessTimeZoneRequest: ResolveBusinessTimeZoneRequest =
                ResolveBusinessTimeZoneRequestSchema.parse(request);
            const query = new URLSearchParams({ country: resolveBusinessTimeZoneRequest.country });
            if (resolveBusinessTimeZoneRequest.zip !== undefined) {
                query.set('zip', resolveBusinessTimeZoneRequest.zip);
            }
            return fetchPillarAPI<ResolveBusinessTimeZoneResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/business-timezone?${query.toString()}`,
                ResolveBusinessTimeZoneResponseSchema
            );
        },
        searchOrganizations: (params: OrganizationOmnisearchSearchRequest) => {
            const requestBody: OrganizationOmnisearchSearchRequest =
                OrganizationOmnisearchSearchRequestSchema.parse(params);
            return fetchPillarAPI<OrganizationOmnisearchPageResponse>(
                '/portal/v1/user_management/organizations/search',
                OrganizationOmnisearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        listSubsidiaryIsosPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return fetchPillarAPI<ListSubsidiaryIsosPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_isos?${queryParams.toString()}`,
                ListSubsidiaryIsosPageResponseSchema
            );
        },

        listSubsidiaryMerchantsPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return fetchPillarAPI<ListSubsidiaryMerchantsPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_merchants?${queryParams.toString()}`,
                ListSubsidiaryMerchantsPageResponseSchema
            );
        },

        listDirectOrganizationUsersPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string; include_descendants?: boolean }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.include_descendants !== undefined) {
                queryParams.set(
                    'include_descendants',
                    params.include_descendants ? 'true' : 'false'
                );
            }
            return fetchPillarAPI<ListDirectOrganizationUsersPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users?${queryParams.toString()}`,
                ListDirectOrganizationUsersPageResponseSchema
            );
        },

        previewOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: PreviewOrganizationUsersCsvImportRequest
        ) =>
            fetchPillarAPI<PreviewOrganizationUsersCsvImportResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users/import-csv/preview`,
                PreviewOrganizationUsersCsvImportResponseSchema,
                {
                    method: 'POST',
                    body: PreviewOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        applyOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: ApplyOrganizationUsersCsvImportRequest
        ) =>
            fetchPillarAPI<ApplyOrganizationUsersCsvImportResponse>(
                `/portal/v2/user_management/organization/${organizationUuid}/users/import-csv/apply`,
                ApplyOrganizationUsersCsvImportResponseV2Schema,
                {
                    method: 'POST',
                    body: ApplyOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        listDocumentTemplates: (
            organizationUuid: string,
            params: ListOrganizationDocumentTemplatesRequest = {}
        ) => {
            const validatedParams = ListOrganizationDocumentTemplatesRequestSchema.parse(params);
            const queryParams = new URLSearchParams();
            if (validatedParams.show_deleted === true) {
                queryParams.set('show_deleted', 'true');
            }
            const serializedQuery = queryParams.toString();
            return fetchPillarAPI<ListOrganizationDocumentTemplatesResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates${
                    serializedQuery.length === 0 ? '' : `?${serializedQuery}`
                }`,
                ListOrganizationDocumentTemplatesResponseSchema
            );
        },

        validateDocumentTemplate: (
            organizationUuid: string,
            body: ValidateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<ValidateOrganizationDocumentTemplateResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/validate`,
                ValidateOrganizationDocumentTemplateResponseSchema,
                {
                    method: 'POST',
                    body: ValidateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        createDocumentTemplate: (
            organizationUuid: string,
            body: CreateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'POST',
                    body: CreateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        updateDocumentTemplate: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: UpdateOrganizationDocumentTemplateRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: UpdateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        setDocumentTemplateActive: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: SetOrganizationDocumentTemplateActiveRequest
        ) =>
            fetchPillarAPI<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}/active`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: SetOrganizationDocumentTemplateActiveRequestSchema.parse(body),
                }
            ),

        getOrganization: (organizationUuid: string) =>
            fetchPillarAPI<GetOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                GetOrganizationResponseSchema
            ),

        createIso: dedupe((data: CreateIsoRequest) =>
            fetchPillarAPI<CreateIsoResponse>(
                '/portal/v1/user_management/pillar_admin/create_iso',
                CreateIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        createMerchant: dedupe((data: CreateMerchantRequest) =>
            fetchPillarAPI<CreateMerchantResponse>(
                '/portal/v1/user_management/pillar_admin/create_merchant',
                CreateMerchantResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        assignMerchantToIso: (data: AssignMerchantToIsoRequest) =>
            fetchPillarAPI<AssignMerchantToIsoResponse>(
                '/portal/v1/user_management/pillar_admin/assign_merchant_to_iso',
                AssignMerchantToIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setOrganizationParent: (data: SetOrganizationParentRequest) =>
            fetchPillarAPI<SetOrganizationParentResponse>(
                '/portal/v1/user_management/pillar_admin/set_organization_parent',
                SetOrganizationParentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateOrganization: dedupe((organizationUuid: string, data: UpdateOrganizationRequest) =>
            fetchPillarAPI<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteOrganization: (organizationUuid: string) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteOrganization: (organizationUuid: string) =>
            fetchPillarAPI<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/undelete`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'POST',
                }
            ),

        exportMerchantBackup: (
            organizationUuid: string,
            request: ExportMerchantBackupRequest = {}
        ) => {
            const parsedRequest: ExportMerchantBackupRequest =
                ExportMerchantBackupRequestSchema.parse(request);
            const searchParams = new URLSearchParams();
            if (parsedRequest.mode !== undefined) {
                searchParams.set('mode', parsedRequest.mode);
            }
            const queryString = searchParams.toString();
            return fetchPillarAPI<ExportMerchantBackupResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-backup/export${
                    queryString.length === 0 ? '' : `?${queryString}`
                }`,
                ExportMerchantBackupResponseSchema
            );
        },

        getMerchantChecklist: (organizationUuid: string) =>
            fetchPillarAPI<GetMerchantChecklistResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist`,
                GetMerchantChecklistResponseSchema
            ),

        getMerchantChecklistCheck: (
            organizationUuid: string,
            checkCode: MerchantChecklistCheckCode,
            options: Pick<PillarApiRequestOptions, 'signal'> = {}
        ) =>
            fetchPillarAPI<GetMerchantChecklistCheckResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist/check/${checkCode}`,
                GetMerchantChecklistCheckResponseSchema,
                options
            ),

        restoreMerchantBackupDryRun: (payload: RestoreMerchantBackupDryRunRequest) =>
            fetchPillarAPI<RestoreMerchantBackupDryRunResponse>(
                '/portal/v1/user_management/merchant-backup/restore/dry-run',
                RestoreMerchantBackupDryRunResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupDryRunRequestSchema.parse(payload),
                }
            ),

        restoreMerchantBackupApply: (payload: RestoreMerchantBackupApplyRequest) =>
            fetchPillarAPI<RestoreMerchantBackupApplyResponse>(
                '/portal/v1/user_management/merchant-backup/restore/apply',
                RestoreMerchantBackupApplyResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupApplyRequestSchema.parse(payload),
                }
            ),
    };

    const customerApi = {
        listCustomerUsersPage: (params: CustomerUsersSearchRequest = {}) => {
            const requestBody: CustomerUsersSearchRequest =
                CustomerUsersSearchRequestSchema.parse(params);
            return fetchPillarAPI<CustomerUsersPageResponse>(
                '/portal/v1/user_management/customer-users/search',
                CustomerUsersPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchCustomers: (params: MerchantCustomerRecordSearchRequest = {}) => {
            const requestBody: MerchantCustomerRecordSearchRequest =
                MerchantCustomerRecordSearchRequestSchema.parse(params);
            return fetchPillarAPI<MerchantCustomerRecordSearchPageResponse>(
                '/portal/v1/user_management/customers/search',
                MerchantCustomerRecordSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        lookupIpwCustomer: (params: MerchantCustomerRecordIpwLookupRequest) => {
            const requestBody: MerchantCustomerRecordIpwLookupRequest =
                MerchantCustomerRecordIpwLookupRequestSchema.parse(params);
            return fetchPillarAPI<MerchantCustomerRecordIpwLookupResponse>(
                '/portal/v1/user_management/customers/ipw/lookup',
                MerchantCustomerRecordIpwLookupResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomer: (merchantCustomerRecordUuid: string) =>
            fetchPillarAPI<MerchantCustomerRecordDetailsResponse>(
                `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                MerchantCustomerRecordDetailsResponseSchema
            ),

        updateCustomer: dedupe(
            (merchantCustomerRecordUuid: string, data: UpdateMerchantCustomerRecordRequest) =>
                fetchPillarAPI<MerchantCustomerRecordDetailsResponse>(
                    `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                    MerchantCustomerRecordDetailsResponseSchema,
                    {
                        method: 'PATCH',
                        body: UpdateMerchantCustomerRecordRequestSchema.parse(data),
                    }
                )
        ),

        upsertIpwCustomer: dedupe((data: UpsertIpwMerchantCustomerRecordRequest) =>
            fetchPillarAPI<UpsertIpwMerchantCustomerRecordResponse>(
                '/portal/v1/user_management/customers/ipw/upsert',
                UpsertIpwMerchantCustomerRecordResponseSchema,
                {
                    method: 'POST',
                    body: UpsertIpwMerchantCustomerRecordRequestSchema.parse(data),
                }
            )
        ),
    };

    const parseLogicalTerminalUuidPathSegment = (logicalTerminalUuid: string): string =>
        LogicalTerminalUuidParamSchema.parse(logicalTerminalUuid);

    const paymentsInfrastructureApi = {
        searchProcessors: (params: ProcessorSearchRequest = {}) => {
            const parseResult = ProcessorSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(`Invalid processor search request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<ProcessorSearchPageResponse>(
                '/portal/v1/payments_infrastructure/processors/search',
                ProcessorSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getProcessor: (processorName: string) =>
            fetchPillarAPI<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema
            ),

        createProcessor: dedupe((payload: CreateProcessorRequest) => {
            const parseResult = CreateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(`Invalid create processor request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<GetProcessorResponse>(
                '/portal/v1/payments_infrastructure/processors',
                GetProcessorResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateProcessor: dedupe((processorName: string, payload: UpdateProcessorRequest) => {
            const parseResult = UpdateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(`Invalid update processor request: ${parseResult.error.message}`);
            }
            return fetchPillarAPI<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        searchMerchantAccounts: (params: MerchantAccountSearchRequest = {}) => {
            const parseResult = MerchantAccountSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant account search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<MerchantAccountSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts/search',
                MerchantAccountSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema
            ),

        createMerchantAccount: dedupe((payload: CreateMerchantAccountRequest) => {
            const parseResult = CreateMerchantAccountRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create merchant account request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetMerchantAccountResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts',
                GetMerchantAccountResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateMerchantAccount: dedupe(
            (merchantAccountUuid: string, payload: UpdateMerchantAccountRequest) => {
                const parseResult = UpdateMerchantAccountRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update merchant account request: ${parseResult.error.message}`
                    );
                }
                return fetchPillarAPI<GetMerchantAccountResponse>(
                    `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                        merchantAccountUuid
                    )}`,
                    GetMerchantAccountResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteMerchantAccount: (merchantAccountUuid: string) =>
            fetchPillarAPI<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}/undelete`,
                GetMerchantAccountResponseSchema,
                { method: 'POST' }
            ),

        searchLogicalTerminals: (params: LogicalTerminalSearchRequest = {}) => {
            const parseResult = LogicalTerminalSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<LogicalTerminalSearchPageResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals/search',
                LogicalTerminalSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchMerchantOrganizations: (params: MerchantOrganizationSearchRequest) => {
            const parseResult = MerchantOrganizationSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant organization search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<MerchantOrganizationSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_organizations/search',
                MerchantOrganizationSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema
            ),

        createLogicalTerminal: dedupe((payload: CreateLogicalTerminalRequest) => {
            const parseResult = CreateLogicalTerminalRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create logical terminal request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetLogicalTerminalResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals',
                GetLogicalTerminalResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateLogicalTerminal: dedupe(
            (logicalTerminalUuid: string, payload: UpdateLogicalTerminalRequest) => {
                const parseResult = UpdateLogicalTerminalRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update logical terminal request: ${parseResult.error.message}`
                    );
                }
                return fetchPillarAPI<GetLogicalTerminalResponse>(
                    `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                        logicalTerminalUuid
                    )}`,
                    GetLogicalTerminalResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteLogicalTerminal: (logicalTerminalUuid: string) =>
            fetchPillarAPI<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/undelete`,
                GetLogicalTerminalResponseSchema,
                { method: 'POST' }
            ),

        previewLogicalTerminalOrganizationChange: (
            logicalTerminalUuid: string,
            payload: PreviewLogicalTerminalOrganizationChangeRequest
        ) => {
            const parseResult =
                PreviewLogicalTerminalOrganizationChangeRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal organization preview request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<PreviewLogicalTerminalOrganizationChangeResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/organization-change-preview`,
                PreviewLogicalTerminalOrganizationChangeResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchTerminalDevices: (params: TerminalDeviceSearchRequest = {}) => {
            const parseResult = TerminalDeviceSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid terminal device search request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<TerminalDeviceSearchPageResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices/search',
                TerminalDeviceSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema
            ),

        checkLogicalTerminalCommandReadiness: (logicalTerminalUuid: string) => {
            const resourceUuid = parseLogicalTerminalUuidPathSegment(logicalTerminalUuid);
            const request = TerminalCommandReadinessRequestV1Schema.parse({
                terminal_command_readiness_version: TerminalCommandReadinessVersion,
                operation: TerminalCommandReadinessRequestOperation.ONE_RESOURCE,
                resource_kind: TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL,
                resource_uuid: resourceUuid,
            });
            return fetchPillarAPI<TerminalCommandReadinessResultV1>(
                `/portal/v1/payments_infrastructure/logical_terminals/${resourceUuid}/command-readiness`,
                TerminalCommandReadinessResultV1Schema,
                { method: 'POST', body: request }
            );
        },

        checkTerminalDeviceCommandReadiness: (terminalDeviceUuid: string) => {
            const resourceUuid = TerminalDeviceUuidParamSchema.parse(terminalDeviceUuid);
            const request = TerminalCommandReadinessRequestV1Schema.parse({
                terminal_command_readiness_version: TerminalCommandReadinessVersion,
                operation: TerminalCommandReadinessRequestOperation.ONE_RESOURCE,
                resource_kind: TerminalCommandReadinessResourceKind.TERMINAL_DEVICE,
                resource_uuid: resourceUuid,
            });
            return fetchPillarAPI<TerminalCommandReadinessResultV1>(
                `/portal/v1/payments_infrastructure/terminal_devices/${resourceUuid}/command-readiness`,
                TerminalCommandReadinessResultV1Schema,
                { method: 'POST', body: request }
            );
        },

        checkMerchantChecklistTerminalCommandReadinessPage: (
            request: TerminalCommandReadinessChecklistPageRequestV1
        ) => {
            const parsedRequest = TerminalCommandReadinessRequestV1Schema.parse(request);
            if (
                parsedRequest.operation !== TerminalCommandReadinessRequestOperation.CHECKLIST_PAGE
            ) {
                throw new Error('Expected a terminal command-readiness checklist page request.');
            }
            return fetchPillarAPI<TerminalCommandReadinessPageV1>(
                `/portal/v1/user_management/organization/${OrganizationUuidParamSchema.parse(
                    parsedRequest.organization_uuid
                )}/merchant-checklist/terminal-command-readiness`,
                TerminalCommandReadinessPageV1Schema,
                { method: 'POST', body: parsedRequest }
            );
        },

        getCloverReadiness: (merchantOrganizationUuid: string, terminalDeviceUuid: string) =>
            fetchPillarAPI<CloverReadinessResult>(
                `/portal/v1/payments_infrastructure/merchant_organizations/${OrganizationUuidParamSchema.parse(
                    merchantOrganizationUuid
                )}/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/clover/readiness`,
                CloverReadinessResultSchema,
                { preserveLosslessNumbers: true }
            ),

        getCloverOAuthAuthorizationStatus: (merchantOrganizationUuid: string) =>
            fetchPillarAPI<CloverOAuthAuthorizationStatus>(
                `/clover/oauth/status?${new URLSearchParams({
                    organization_uuid: OrganizationUuidParamSchema.parse(merchantOrganizationUuid),
                }).toString()}`,
                CloverOAuthAuthorizationStatusSchema
            ),

        renewCloverOAuthAuthorization: (merchantOrganizationUuid: string) => {
            const request = RenewCloverOAuthAuthorizationRequestSchema.parse({
                organization_uuid: merchantOrganizationUuid,
            });
            return fetchPillarAPI<RenewCloverOAuthAuthorizationResponse>(
                '/clover/oauth/renew',
                RenewCloverOAuthAuthorizationResponseSchema,
                { method: 'POST', body: request }
            );
        },

        getCloverPaymentsInfrastructureStatus: (merchantOrganizationUuid: string) =>
            fetchPillarAPI<CloverPaymentsInfrastructureStatus>(
                `/clover/oauth/infrastructure?${new URLSearchParams({
                    organization_uuid: OrganizationUuidParamSchema.parse(merchantOrganizationUuid),
                }).toString()}`,
                CloverPaymentsInfrastructureStatusSchema
            ),

        synchronizeCloverPaymentsInfrastructure: (merchantOrganizationUuid: string) => {
            const request = CloverPaymentsInfrastructureRequestSchema.parse({
                organization_uuid: merchantOrganizationUuid,
            });
            return fetchPillarAPI<CloverPaymentsInfrastructureSynchronizationResult>(
                '/clover/oauth/infrastructure/synchronize',
                CloverPaymentsInfrastructureSynchronizationResultSchema,
                { method: 'POST', body: request }
            );
        },

        invalidateCloverOAuthAuthorization: (merchantOrganizationUuid: string) =>
            fetchPillarAPI<CloverOAuthAuthorizationStatus>(
                '/clover/oauth/invalidate',
                CloverOAuthAuthorizationStatusSchema,
                {
                    method: 'POST',
                    body: {
                        organization_uuid:
                            OrganizationUuidParamSchema.parse(merchantOrganizationUuid),
                    },
                }
            ),

        updateCloverOAuthRefreshInterval: (request: UpdateCloverOAuthRefreshIntervalRequest) => {
            const validatedRequest = UpdateCloverOAuthRefreshIntervalRequestSchema.parse(request);
            return fetchPillarAPI<CloverOAuthAuthorizationStatus>(
                '/clover/oauth/refresh-interval',
                CloverOAuthAuthorizationStatusSchema,
                { method: 'PATCH', body: validatedRequest }
            );
        },

        createTerminalDevice: dedupe((payload: CreateTerminalDeviceRequest) => {
            const parseResult = CreateTerminalDeviceRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create terminal device request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<GetTerminalDeviceResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices',
                GetTerminalDeviceResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateTerminalDevice: dedupe(
            (terminalDeviceUuid: string, payload: UpdateTerminalDeviceRequest) => {
                const parseResult = UpdateTerminalDeviceRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update terminal device request: ${parseResult.error.message}`
                    );
                }
                return fetchPillarAPI<GetTerminalDeviceResponse>(
                    `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                        terminalDeviceUuid
                    )}`,
                    GetTerminalDeviceResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteTerminalDevice: (terminalDeviceUuid: string) =>
            fetchPillarAPI<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/undelete`,
                GetTerminalDeviceResponseSchema,
                { method: 'POST' }
            ),
    };

    const sambaConfigApi = {
        getIsoAdminRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        getIsoAdminRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<MerchantConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        updateIsoAdminRoleAssignmentConfig: (
            roleAssignmentUuid: string,
            config: RoleAssignmentCustomConfig,
            expectedConfig: RoleAssignmentCustomConfig
        ) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PUT',
                    body: UpdateRoleAssignmentConfigRequestSchema.parse({
                        expected_config: expectedConfig,
                        config,
                    } satisfies UpdateRoleAssignmentConfigRequest),
                }
            ),

        getIsoAdminOrgConfig: (organizationUuid: string) =>
            fetchPillarAPI<MerchantConfig>(
                `/portal/v1/samba_config/iso_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        getRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        updateRoleAssignmentConfig: (
            roleAssignmentUuid: string,
            config: RoleAssignmentCustomConfig,
            expectedConfig: RoleAssignmentCustomConfig
        ) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PUT',
                    body: UpdateRoleAssignmentConfigRequestSchema.parse({
                        expected_config: expectedConfig,
                        config,
                    } satisfies UpdateRoleAssignmentConfigRequest),
                }
            ),

        getOrgConfig: (organizationUuid: string) =>
            fetchPillarAPI<MerchantConfig>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        getCloverRuntimeEnvironmentAvailability: (organizationUuid: string) =>
            fetchPillarAPI<CloverRuntimeEnvironmentAvailabilityResponse>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}/clover_runtime_availability`,
                CloverRuntimeEnvironmentAvailabilityResponseSchema
            ),

        getProductionPresetAvailability: (organizationUuid: string) =>
            fetchPillarAPI<ProductionPresetAvailabilityResponse>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}/production_preset_availability`,
                ProductionPresetAvailabilityResponseSchema
            ),

        getMyUserConfig: () =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                '/portal/v1/samba_config/any_user/user',
                RoleAssignmentUserConfigResponseSchema
            ),

        getMyOrgConfig: () =>
            fetchPillarAPI<MerchantConfig>(
                '/portal/v1/samba_config/any_user/org',
                ConfigResponseSchema
            ),

        getMyEffectiveConfig: () =>
            fetchPillarAPI<EffectiveConfigResponse>(
                '/portal/v2/samba_config/any_user/effective',
                EffectiveConfigResponseSchema
            ),

        getMyNavigateMenuWebsites: () =>
            fetchPillarAPI<NavigateMenuWebsitesResponse>(
                '/portal/v1/samba_config/any_user/navigate_menu',
                NavigateMenuWebsitesResponseSchema
            ),

        getMyCombinedConfig: () =>
            fetchPillarAPI<CombinedConfigResponse>(
                '/portal/v1/samba_config/any_user/combined',
                CombinedConfigResponseSchema
            ),

        getMyRoleAssignmentUserConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        getMyRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            fetchPillarAPI<MerchantConfig>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        patchMyActiveRoleAssignmentUserRoleAssignmentConfig: (
            request: PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest
        ) =>
            fetchPillarAPI<RoleAssignmentCustomConfig>(
                '/portal/v1/samba_config/any_user/active_role_assignment/user_role_assignment_config',
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PATCH',
                    body: PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema.parse(
                        request
                    ),
                }
            ),

        getCombinedConfigForUser: (userId: number) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/effective_config/${userId}`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionOther: (sessionUuid: string) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/admin/session/${sessionUuid}/config/combined`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionSelf: (auditId: number) =>
            fetchPillarAPI<CombinedConfigResponse>(
                `/portal/v1/samba_config/any_user/session/${auditId}/config/combined`,
                CombinedConfigResponseSchema
            ),
    };

    const paymentsApi = {
        getPaymentRequestDeliveryChannels: async (
            activeRoleAssignmentOperationalResponse: ActiveRoleAssignmentOperationalResponse,
            merchantOrganizationUuid: string
        ): Promise<readonly CustomerPortalDeliveryChannel[]> => {
            let merchantConfig: MerchantConfig;
            if (
                activeRoleAssignmentOperationalResponse.organization_uuid ===
                merchantOrganizationUuid
            ) {
                merchantConfig = activeRoleAssignmentOperationalResponse.combined_config.org_config;
            } else if (isInternalRole(activeRoleAssignmentOperationalResponse.role)) {
                merchantConfig = await sambaConfigApi.getOrgConfig(merchantOrganizationUuid);
            } else {
                throw new Error(
                    'The payment request Merchant does not match the active organization.'
                );
            }
            return getEnabledPaymentRequestDeliveryChannels(
                merchantConfig.platformConfig.features.customerPortal?.paymentRequests
            );
        },

        processOutstandingCloverWebhooks: () =>
            fetchPillarAPI<CloverWebhookProcessingResult>(
                '/portal/v1/internal/clover-webhooks/process',
                CloverWebhookProcessingResultSchema,
                { method: 'POST' }
            ),

        getVaultTransactions: (params: GetVaultTransactionsSearchRequest = {}) => {
            const requestBody: GetVaultTransactionsSearchRequest =
                GetVaultTransactionsSearchRequestSchema.parse(params);
            return fetchPillarAPI<GetVaultTransactionsResponse>(
                '/portal/v1/payments/vault/transactions/search',
                GetVaultTransactionsResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        listPaymentTransactionLifecycleEvents: (
            params: PaymentTransactionLifecycleEventSearchRequest = {}
        ) => {
            const requestBody: PaymentTransactionLifecycleEventSearchRequest =
                PaymentTransactionLifecycleEventSearchRequestSchema.parse(params);
            return fetchPillarAPI<PaymentTransactionLifecycleEventListResponse>(
                '/portal/v1/payments/transaction-lifecycle-events/search',
                PaymentTransactionLifecycleEventListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPaymentAuditLog: (params: GetPaymentAuditLogSearchRequest) => {
            const requestBody: GetPaymentAuditLogSearchRequest =
                GetPaymentAuditLogSearchRequestSchema.parse(params);
            return fetchPillarAPI<GetPaymentAuditLogResponse>(
                '/portal/v1/payments/audit-log/search',
                GetPaymentAuditLogResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getSavedCardPaymentMethods: (params: GetSavedCardPaymentMethodsRequest) => {
            const queryParams = new URLSearchParams({
                customer_name: params.customer_name,
                ipw_system_id: params.ipw_system_id.toString(),
                terminal_device_uuid: params.terminal_device_uuid,
            });
            if (params.phone_number !== undefined) {
                queryParams.set('phone_number', params.phone_number);
            }
            if (params.email_address !== undefined) {
                queryParams.set('email_address', params.email_address);
            }
            return fetchPillarAPI<GetSavedCardPaymentMethodsResponse>(
                `/portal/v1/payments/method/saved-card?${queryParams.toString()}`,
                GetSavedCardPaymentMethodsResponseSchema
            );
        },

        getDelegateToken: dedupe((data: GetDelegateTokenRequest) =>
            fetchPillarAPI<GetDelegateTokenResponse>(
                '/portal/v1/payments/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        binLookup: dedupe((data: BinLookupRequest) =>
            fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        hostedCardTokenQuote: dedupe((data: HostedCardTokenQuoteRequest) =>
            fetchPillarAPI<HostedCardTokenQuoteResponse>(
                '/portal/v1/payments/card-token-quote',
                HostedCardTokenQuoteResponseSchema,
                {
                    method: 'POST',
                    body: HostedCardTokenQuoteRequestSchema.parse(data),
                    preserveLosslessNumbers: true,
                }
            )
        ),

        getPaymentConfiguration: (params: GetPaymentConfigurationRequest) => {
            const validatedParams: GetPaymentConfigurationRequest =
                GetPaymentConfigurationRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                terminal_device_uuid: validatedParams.terminal_device_uuid,
            });
            return fetchPillarAPI<PaymentConfigurationResponse>(
                `/portal/v1/payments/payment-configuration?${queryParams.toString()}`,
                PaymentConfigurationResponseSchema
            );
        },

        getPaymentActionReadiness: (terminalDeviceUuid: string) =>
            fetchPillarAPI<PaymentActionReadinessResponse>(
                `/portal/v1/payments/terminal-devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/action-readiness/v3`,
                PaymentActionReadinessResponseSchema
            ),

        getSelectedPaymentActionReadiness: async (
            terminalDeviceUuid: string,
            paymentReadinessAction: PaymentReadinessAction
        ) => {
            const parsedTerminalDeviceUuid: string =
                TerminalDeviceUuidParamSchema.parse(terminalDeviceUuid);
            const parsedPaymentReadinessAction: PaymentReadinessAction =
                PaymentReadinessActionSchema.parse(paymentReadinessAction);
            const selectedPaymentActionReadinessResponseV1: SelectedPaymentActionReadinessResponseV1 =
                await fetchPillarAPI<SelectedPaymentActionReadinessResponseV1>(
                    `/portal/v1/payments/terminal-devices/${parsedTerminalDeviceUuid}/action-readiness/actions/${parsedPaymentReadinessAction}/v1`,
                    SelectedPaymentActionReadinessResponseV1Schema
                );
            if (selectedPaymentActionReadinessResponseV1.action !== parsedPaymentReadinessAction) {
                throw new Error('Payment readiness response action does not match request.');
            }
            return selectedPaymentActionReadinessResponseV1.availability;
        },

        createDraftPaymentRequest: (data: CreateDraftPaymentRequestRequest) => {
            const validatedData: CreateDraftPaymentRequestRequest =
                CreateDraftPaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<CreateDraftPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/draft',
                CreateDraftPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        getPaymentRequestOwnerCandidates: () =>
            fetchPillarAPI<PaymentRequestOwnerCandidatesResponse>(
                '/portal/v1/payments/payment-requests/owner-candidates',
                PaymentRequestOwnerCandidatesResponseSchema
            ),

        uploadDraftPaymentRequestDocument: (paymentRequestUuid: string, file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            return fetchPillarAPI<UploadPaymentRequestDocumentResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/documents/upload`,
                UploadPaymentRequestDocumentResponseSchema,
                {
                    method: 'POST',
                    body: formData,
                }
            );
        },

        finalizeSendPaymentRequest: (data: FinalizeSendPaymentRequestRequest) => {
            const validatedData: FinalizeSendPaymentRequestRequest =
                FinalizeSendPaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<FinalizeSendPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/finalize-send',
                FinalizeSendPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        listMerchantPaymentRequests: (params: MerchantPaymentRequestSearchRequest = {}) => {
            const requestBody: MerchantPaymentRequestSearchRequest =
                MerchantPaymentRequestSearchRequestSchema.parse(params);
            return fetchPillarAPI<MerchantPaymentRequestListResponse>(
                '/portal/v1/payments/payment-requests/search',
                MerchantPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getMerchantPaymentRequestDetails: (
            paymentRequestUuid: string,
            params: MerchantPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: MerchantPaymentRequestDetailsRequest =
                MerchantPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/payment-requests/${paymentRequestUuid}?${querySuffix}`
                    : `/portal/v1/payments/payment-requests/${paymentRequestUuid}`;
            return fetchPillarAPI<PaymentRequestDetailsView>(
                endpoint,
                PaymentRequestDetailsViewSchema
            );
        },

        getMerchantPaymentRequestDeliveryEvents: (
            paymentRequestUuid: string,
            paymentRequestDeliveryEventListRequest: PaymentRequestDeliveryEventListRequest
        ) => {
            const validatedPaymentRequestDeliveryEventListRequest: PaymentRequestDeliveryEventListRequest =
                PaymentRequestDeliveryEventListRequestSchema.parse(
                    paymentRequestDeliveryEventListRequest
                );
            const queryParams: URLSearchParams = new URLSearchParams();
            queryParams.set(
                'contract_version',
                validatedPaymentRequestDeliveryEventListRequest.contract_version
            );
            queryParams.set('limit', String(validatedPaymentRequestDeliveryEventListRequest.limit));
            if (validatedPaymentRequestDeliveryEventListRequest.cursor !== undefined) {
                queryParams.set('cursor', validatedPaymentRequestDeliveryEventListRequest.cursor);
            }
            if (
                validatedPaymentRequestDeliveryEventListRequest.merchant_organization_uuid !==
                undefined
            ) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedPaymentRequestDeliveryEventListRequest.merchant_organization_uuid
                );
            }
            return fetchPillarAPI<PaymentRequestDeliveryEventList>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/delivery-events?${queryParams.toString()}`,
                PaymentRequestDeliveryEventListSchema
            );
        },

        resendPaymentRequest: (
            paymentRequestUuid: string,
            data: ResendPaymentRequestRequest,
            merchantPaymentRequestDetailsRequest: MerchantPaymentRequestDetailsRequest = {}
        ) => {
            const validatedData: ResendPaymentRequestRequest =
                ResendPaymentRequestRequestSchema.parse(data);
            const validatedParams: MerchantPaymentRequestDetailsRequest =
                MerchantPaymentRequestDetailsRequestSchema.parse(
                    merchantPaymentRequestDetailsRequest
                );
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/resend` +
                (querySuffix.length === 0 ? '' : `?${querySuffix}`);
            return fetchPillarAPI<FinalizeSendPaymentRequestResponse>(
                endpoint,
                FinalizeSendPaymentRequestResponseSchema,
                { method: 'POST', body: validatedData }
            );
        },

        cancelAndReissuePaymentRequest: (
            paymentRequestUuid: string,
            data: CancelAndReissuePaymentRequestRequest
        ) => {
            const validatedData: CancelAndReissuePaymentRequestRequest =
                CancelAndReissuePaymentRequestRequestSchema.parse(data);
            return fetchPillarAPI<CancelAndReissuePaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/cancel-and-reissue`,
                CancelAndReissuePaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        revokePaymentRequestLink: (
            paymentRequestUuid: string,
            params: MerchantPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: MerchantPaymentRequestDetailsRequest =
                MerchantPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/payment-requests/${paymentRequestUuid}/revoke-payment-link?${querySuffix}`
                    : `/portal/v1/payments/payment-requests/${paymentRequestUuid}/revoke-payment-link`;
            return fetchPillarAPI<MessageResponse>(endpoint, MessageResponseSchema, {
                method: 'POST',
            });
        },

        bootstrapPaymentRequestGuestSession: (tokenValue: string) =>
            fetchPillarAPI<PublicPaymentRequestView>(
                `/portal/v1/payments/public/payment-request-links/${tokenValue}/bootstrap`,
                PublicPaymentRequestViewSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentPublicPaymentRequest: () =>
            fetchPillarAPI<PublicPaymentRequestView>(
                '/portal/v1/payments/public/payment-request-links/current',
                PublicPaymentRequestViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        requestCurrentPaymentRequestReplacementLink: () =>
            fetchPillarAPI<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/request-new-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        resendCurrentReissuedPaymentRequestLink: () =>
            fetchPillarAPI<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/resend-reissued-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentEntryContext: () =>
            fetchPillarAPI<GuestPaymentEntryContextView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-entry-context',
                GuestPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentAuthenticatedPaymentEntryContext: () =>
            fetchPillarAPI<AuthenticatedPaymentEntryContextView>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment-entry-context',
                AuthenticatedPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentSuccessView: () =>
            fetchPillarAPI<GuestPaymentSuccessView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-success',
                GuestPaymentSuccessViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestReceiptHTML: () =>
            requestTextWithSchema(
                '/portal/v1/payments/public/payment-request-links/current/receipt-html',
                z.string().min(1),
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentRequestDocumentAccess: async (
            paymentRequestDocumentUuid: string
        ): Promise<GuestPaymentRequestDocumentAccessResponse> =>
            schedulePillarApiRequest(async () => {
                const validatedPaymentRequestDocumentUuid: string = z
                    .string()
                    .uuid()
                    .parse(paymentRequestDocumentUuid);
                const endpoint: string =
                    '/portal/v1/payments/public/payment-request-links/documents/' +
                    `${validatedPaymentRequestDocumentUuid}/signed-url`;
                const response: Response = await fetchImplementation(buildRequestUrl(endpoint), {
                    method: 'GET',
                    credentials: 'include',
                    headers: getRequestHeaders ? getRequestHeaders() : {},
                    redirect: 'manual',
                });

                try {
                    if (onResponse) {
                        onResponse(response);
                    }

                    if (response.status === 401) {
                        const errorMessage: string = response.statusText || 'Unauthorized';
                        throw new ApiError(response.status, response.statusText, errorMessage);
                    }

                    if (response.status >= 300 && response.status < 400) {
                        const locationHeaderValue: string | null = response.headers.get('location');
                        if (
                            locationHeaderValue === null ||
                            locationHeaderValue.trim().length === 0
                        ) {
                            throw new ApiError(
                                response.status,
                                response.statusText,
                                'Redirect response missing Location header'
                            );
                        }
                        await response.arrayBuffer();
                        return GuestPaymentRequestDocumentAccessResponseSchema.parse({
                            kind: 'redirect',
                            url: new URL(locationHeaderValue, buildRequestUrl(endpoint)).toString(),
                        });
                    }

                    if (!response.ok) {
                        const errorText: string = await response.text();
                        const errorMessage: string =
                            errorText.trim().length > 0 ? errorText : response.statusText;
                        throw new ApiError(response.status, response.statusText, errorMessage);
                    }

                    const contentType: string | null = response.headers.get('content-type');
                    if (contentType === null || contentType.trim().length === 0) {
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            'Inline document response missing Content-Type header'
                        );
                    }

                    const contentLengthHeaderValue: string | null =
                        response.headers.get('content-length');
                    const contentLength: number | undefined =
                        contentLengthHeaderValue === null
                            ? undefined
                            : z.coerce.number().int().nonnegative().parse(contentLengthHeaderValue);

                    await response.arrayBuffer();
                    return GuestPaymentRequestDocumentAccessResponseSchema.parse({
                        kind: 'inline',
                        content_type: contentType,
                        content_length: contentLength,
                    });
                } finally {
                    await cancelUnreadPillarApiResponse(response);
                }
            }),

        getCurrentGuestDelegateToken: () =>
            fetchPillarAPI<GetDelegateTokenResponse>(
                '/portal/v1/payments/public/payment-request-links/current/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        guestBinLookup: (data: GuestBinLookupRequest) => {
            const validatedData: GuestBinLookupRequest = GuestBinLookupRequestSchema.parse(data);
            return fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/public/payment-request-links/current/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        guestHostedCardTokenQuote: (data: GuestHostedCardTokenQuoteRequest) => {
            const validatedData = GuestHostedCardTokenQuoteRequestSchema.parse(data);
            return fetchPillarAPI<HostedCardTokenQuoteResponse>(
                '/portal/v1/payments/public/payment-request-links/current/card-token-quote',
                HostedCardTokenQuoteResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                    preserveLosslessNumbers: true,
                }
            );
        },

        processCurrentGuestCardTokenPayment: (data: GuestProcessCardTokenPaymentRequest) => {
            const validatedData: GuestProcessCardTokenPaymentRequest =
                GuestProcessCardTokenPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/public/payment-request-links/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentAuthenticatedCardTokenPayment: (
            data: AuthenticatedProcessCardTokenPaymentRequest
        ) => {
            const validatedData: AuthenticatedProcessCardTokenPaymentRequest =
                AuthenticatedProcessCardTokenPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentStoredPaymentMethodPayment: (
            data: GuestProcessStoredPaymentMethodPaymentRequest
        ) => {
            const validatedData: GuestProcessStoredPaymentMethodPaymentRequest =
                GuestProcessStoredPaymentMethodPaymentRequestSchema.parse(data);
            return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/stored-method',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        listCardPaymentTerminalDevices: (params: ListCardPaymentTerminalDevicesRequest) => {
            const validatedParams: ListCardPaymentTerminalDevicesRequest =
                ListCardPaymentTerminalDevicesRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
                action: validatedParams.action,
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.resource_type !== undefined) {
                queryParams.set('resource_type', validatedParams.resource_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            if (validatedParams.include_empty_reason !== undefined) {
                queryParams.set(
                    'include_empty_reason',
                    String(validatedParams.include_empty_reason)
                );
            }
            return fetchPillarAPI<ListCardPaymentTerminalDevicesResponse>(
                `/portal/v1/payments/card-payment/terminals?${queryParams.toString()}`,
                ListCardPaymentTerminalDevicesResponseSchema
            );
        },

        getAccessibleCredentialsInfo: (params: ListAccessibleCredentialsInfoRequest) => {
            const validatedParams: ListAccessibleCredentialsInfoRequest =
                ListAccessibleCredentialsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.credential_type !== undefined) {
                queryParams.set('credential_type', validatedParams.credential_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return fetchPillarAPI<ListAccessibleCredentialsInfoResponse>(
                `/portal/v1/payments/info/accessible-credentials?${queryParams.toString()}`,
                ListAccessibleCredentialsInfoResponseSchema
            );
        },

        getAccessibleTerminalsInfo: (params: ListAccessibleTerminalsInfoRequest) => {
            const validatedParams: ListAccessibleTerminalsInfoRequest =
                ListAccessibleTerminalsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.action !== undefined) {
                queryParams.set('action', validatedParams.action);
            }
            if (validatedParams.resource_type !== undefined) {
                queryParams.set('resource_type', validatedParams.resource_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            if (validatedParams.include_empty_reason !== undefined) {
                queryParams.set(
                    'include_empty_reason',
                    String(validatedParams.include_empty_reason)
                );
            }
            return fetchPillarAPI<ListAccessibleTerminalsInfoResponse>(
                `/portal/v1/payments/info/accessible-terminals?${queryParams.toString()}`,
                ListAccessibleTerminalsInfoResponseSchema
            );
        },

        getReconcilePaymentsCandidates: (params: GetReconcilePaymentsCandidatesRequest) => {
            const queryParams = new URLSearchParams({
                ipw_system_id: params.ipw_system_id.toString(),
            });
            if ('payment_transaction_uuid' in params) {
                queryParams.set('payment_transaction_uuid', params.payment_transaction_uuid);
            } else {
                queryParams.set('customer_name', params.customer_name);
                if (params.phone_number !== undefined) {
                    queryParams.set('phone_number', params.phone_number);
                }
                if (params.parts_invoice_number !== undefined) {
                    queryParams.set('parts_invoice_number', params.parts_invoice_number);
                }
                if (params.ro_number !== undefined) {
                    queryParams.set('ro_number', params.ro_number);
                }
                if (params.invoice_id !== undefined) {
                    queryParams.set('invoice_id', params.invoice_id);
                }
            }
            if (params.reconciled !== undefined) {
                queryParams.set('reconciled', params.reconciled);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            return fetchPillarAPI<GetReconcilePaymentsCandidatesResponse>(
                `/portal/v1/payments/reconcile/candidates?${queryParams.toString()}`,
                GetReconcilePaymentsCandidatesResponseSchema
            );
        },

        getTransactionDetails: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GetTransactionDetailsResponse>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}`,
                GetTransactionDetailsResponseSchema
            ),

        refreshGatewayTransactionDetails: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GatewayTransactionDetailsFetchResult>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}/gateway-details/refresh`,
                GatewayTransactionDetailsFetchResultSchema,
                {
                    method: 'POST',
                }
            ),

        getReceiptData: (paymentTransactionUuid: string) =>
            fetchPillarAPI<GetReceiptDataResponse>(
                `/portal/v1/payments/vault/receipt-data/${paymentTransactionUuid}`,
                GetReceiptDataResponseSchema
            ),

        getReceiptHTML: (paymentTransactionUuid: string) =>
            requestTextWithSchema(
                `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`,
                z.string().min(1)
            ),

        getReceiptHTMLUrl: (paymentTransactionUuid: string): string => {
            const endpoint = `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`;
            return buildRequestUrl(endpoint).toString();
        },

        refundTransaction: dedupe((data: RefundTransactionRequest) =>
            fetchPillarAPI<RefundTransactionResponse>(
                '/portal/v1/payments/refund',
                RefundTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        refundPaymentRequest: dedupe(
            (paymentRequestUuid: string, data: RefundPaymentRequestRequest) =>
                fetchPillarAPI<RefundPaymentRequestResponse>(
                    `/portal/v1/payments/payment-requests/${paymentRequestUuid}/refund`,
                    RefundPaymentRequestResponseSchema,
                    {
                        method: 'POST',
                        body: data,
                    }
                )
        ),

        resetTransactionReconciliation: dedupe((paymentTransactionUuid: string) =>
            fetchPillarAPI<ResetTransactionReconciliationResponse>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}/reset-reconciliation`,
                ResetTransactionReconciliationResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        resetPaymentRequestReconciliation: dedupe((paymentRequestUuid: string) =>
            fetchPillarAPI<ResetPaymentRequestReconciliationResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/reset-reconciliation`,
                ResetPaymentRequestReconciliationResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        voidTransaction: dedupe((data: VoidTransactionRequest) =>
            fetchPillarAPI<VoidTransactionResponse>(
                '/portal/v1/payments/void',
                VoidTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        voidPaymentRequest: dedupe((paymentRequestUuid: string) =>
            fetchPillarAPI<VoidPaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/void`,
                VoidPaymentRequestResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        terminalSale: dedupe((data: TerminalSaleRequest) =>
            fetchPillarAPI<TerminalSaleResponse>(
                '/portal/v1/payments/terminal/sale',
                TerminalSaleResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        terminalTokenize: dedupe((data: TerminalTokenizationRequest) =>
            fetchPillarAPI<TerminalTokenizationResponse>(
                '/portal/v1/payments/terminal/tokenize',
                TerminalTokenizationResponseSchema,
                {
                    method: 'POST',
                    body: TerminalTokenizationRequestSchema.parse(data),
                }
            )
        ),

        terminalAbort: dedupe((data: TerminalAbortRequest) =>
            fetchPillarAPI<TerminalAbortResponse>(
                '/portal/v1/payments/terminal/abort',
                TerminalAbortResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordToken: dedupe((data: RecordTokenRequest) =>
            fetchPillarAPI<RecordTokenResponse>(
                '/portal/v1/payments/method/card-token',
                RecordTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCardTokenPayment: dedupe((data: SubmitCardTokenPaymentRequest) =>
            fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCloverCardNotPresentPayment: dedupe(
            (data: ProcessCloverCardNotPresentPaymentRequest) =>
                fetchPillarAPI<ProcessCardNotPresentPaymentResponse>(
                    '/portal/v1/payments/payment/clover/card-not-present',
                    ProcessCardNotPresentPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: ProcessCloverCardNotPresentPaymentRequestSchema.parse(data),
                    }
                )
        ),

        processStoredPaymentMethodPayment: dedupe(
            (data: ProcessStoredPaymentMethodPaymentRequest) => {
                const validatedData: ProcessStoredPaymentMethodPaymentRequest =
                    ProcessStoredPaymentMethodPaymentRequestSchema.parse(data);
                return fetchPillarAPI<ProcessCardTokenPaymentResponse>(
                    '/portal/v1/payments/payment/stored-method',
                    ProcessCardTokenPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: validatedData,
                    }
                );
            }
        ),

        recordReconciliation: dedupe((data: RecordReconciliationRequest) =>
            fetchPillarAPI<RecordReconciliationResponse>(
                '/portal/v1/payments/reconcile',
                RecordReconciliationResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordPaymentRequestReconciliation: dedupe(
            (data: RecordPaymentRequestReconciliationRequest) =>
                fetchPillarAPI<RecordPaymentRequestReconciliationResponse>(
                    '/portal/v1/payments/payment-requests/reconcile',
                    RecordPaymentRequestReconciliationResponseSchema,
                    {
                        method: 'POST',
                        body: RecordPaymentRequestReconciliationRequestSchema.parse(data),
                    }
                )
        ),

        processAchPayment: dedupe((data: ProcessAchPaymentRequest) =>
            fetchPillarAPI<ProcessAchPaymentResponse>(
                '/portal/v1/payments/ach',
                ProcessAchPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        listCustomerPaymentMethods: (params: CustomerPaymentMethodListRequest = {}) => {
            const validatedParams: CustomerPaymentMethodListRequest =
                CustomerPaymentMethodListRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods';
            return fetchPillarAPI<CustomerPaymentMethodListResponse>(
                endpoint,
                CustomerPaymentMethodListResponseSchema
            );
        },

        listMerchantPaymentMethodTokens: (params: MerchantPaymentMethodTokenListRequest = {}) =>
            fetchPillarAPI<MerchantPaymentMethodTokenListResponse>(
                '/portal/v1/payments/payment-method-tokens/search',
                MerchantPaymentMethodTokenListResponseSchema,
                {
                    method: 'POST',
                    body: MerchantPaymentMethodTokenListRequestSchema.parse(params),
                }
            ),

        getMerchantPaymentMethodTokenDetails: (paymentMethodTokenUuid: string) =>
            fetchPillarAPI<MerchantPaymentMethodTokenDetailsView>(
                `/portal/v1/payments/payment-method-tokens/${paymentMethodTokenUuid}`,
                MerchantPaymentMethodTokenDetailsViewSchema
            ),

        getCustomerPaymentMethodBootstrap: (params: CustomerPaymentMethodBootstrapRequest = {}) => {
            const validatedParams: CustomerPaymentMethodBootstrapRequest =
                CustomerPaymentMethodBootstrapRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods/add/bootstrap?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods/add/bootstrap';
            return fetchPillarAPI<CustomerPaymentMethodBootstrapResponse>(
                endpoint,
                CustomerPaymentMethodBootstrapResponseSchema
            );
        },

        customerPaymentMethodBinLookup: (data: CustomerPaymentMethodBinLookupRequest) =>
            fetchPillarAPI<BinLookupResponse>(
                '/portal/v1/payments/customer/payment-methods/add/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: CustomerPaymentMethodBinLookupRequestSchema.parse(data),
                }
            ),

        createCustomerPaymentMethod: (data: CreateCustomerPaymentMethodRequest) =>
            fetchPillarAPI<CreateCustomerPaymentMethodResponse>(
                '/portal/v1/payments/customer/payment-methods',
                CreateCustomerPaymentMethodResponseSchema,
                {
                    method: 'POST',
                    body: CreateCustomerPaymentMethodRequestSchema.parse(data),
                }
            ),

        updateCustomerPaymentMethod: dedupe((data: UpdateCustomerPaymentMethodRequest) => {
            const validatedData: UpdateCustomerPaymentMethodRequest =
                UpdateCustomerPaymentMethodRequestSchema.parse(data);
            return fetchPillarAPI<UpdateCustomerPaymentMethodResponse>(
                `/portal/v1/payments/customer/payment-methods/${validatedData.payment_method_token_uuid}`,
                UpdateCustomerPaymentMethodResponseSchema,
                {
                    method: 'PATCH',
                    body: UpdateCustomerPaymentMethodBodyWithScopeSchema.parse(validatedData),
                }
            );
        }),

        deleteCustomerPaymentMethod: dedupe((data: DeleteCustomerPaymentMethodRequest) => {
            const validatedData: DeleteCustomerPaymentMethodRequest =
                DeleteCustomerPaymentMethodRequestSchema.parse(data);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedData.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedData.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                `/portal/v1/payments/customer/payment-methods/${validatedData.payment_method_token_uuid}` +
                (querySuffix.length > 0 ? `?${querySuffix}` : '');
            return fetchPillarAPI<DeleteCustomerPaymentMethodResponse>(
                endpoint,
                DeleteCustomerPaymentMethodResponseSchema,
                { method: 'DELETE' }
            );
        }),

        getCustomerPortalDashboard: (params: CustomerPortalDashboardRequest = {}) => {
            const validatedParams: CustomerPortalDashboardRequest =
                CustomerPortalDashboardRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/dashboard?${querySuffix}`
                    : '/portal/v1/payments/customer/dashboard';
            return fetchPillarAPI<CustomerPortalDashboardResponse>(
                endpoint,
                CustomerPortalDashboardResponseSchema
            );
        },

        listCustomerPaymentRequests: (params: CustomerPaymentRequestSearchRequest = {}) => {
            const requestBody: CustomerPaymentRequestSearchRequest =
                CustomerPaymentRequestSearchRequestSchema.parse(params);
            return fetchPillarAPI<CustomerPaymentRequestListResponse>(
                '/portal/v1/payments/customer/payment-requests/search',
                CustomerPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomerPaymentRequestDetails: (
            paymentRequestUuid: string,
            params: CustomerPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: CustomerPaymentRequestDetailsRequest =
                CustomerPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}?${querySuffix}`
                    : `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}`;
            return fetchPillarAPI<PaymentRequestDetailsView>(
                endpoint,
                PaymentRequestDetailsViewSchema
            );
        },

        listCustomerPaymentHistory: (params: CustomerPaymentHistoryRequest = {}) => {
            const validatedParams: CustomerPaymentHistoryRequest =
                CustomerPaymentHistoryRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-history?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-history';
            return fetchPillarAPI<CustomerPaymentHistoryResponse>(
                endpoint,
                CustomerPaymentHistoryResponseSchema
            );
        },
    };

    const sambaApi = {
        getVersion: (headers?: HttpRequestHeaders) =>
            fetchPillarAPI<SambaVersionResponse>('/samba/v1/version', SambaVersionResponseSchema, {
                headers,
            }),
        listModules: (headers?: HttpRequestHeaders) =>
            fetchPillarAPI<SambaModuleListResponse>(
                '/samba/v1/modules',
                SambaModuleListResponseSchema,
                {
                    headers,
                }
            ),
        /** One vendor-signed workflow container for `platform` (launcher contract v3). */
        getWorkflowRelease: async (
            moduleName: RpaModuleName,
            params: SambaWorkflowReleaseQueryV2,
            headers?: HttpRequestHeaders
        ): Promise<Uint8Array> =>
            schedulePillarApiRequest(async () => {
                const query = SambaWorkflowReleaseQueryV2Schema.parse(params);
                const queryParams = new URLSearchParams({
                    schemaVersion: String(query.schemaVersion),
                    platform: query.platform,
                    workflowProtocolVersion: String(query.workflowProtocolVersion),
                });
                const endpoint = `/samba/v1/workflow-releases/${encodeURIComponent(moduleName)}?${queryParams.toString()}`;
                const response: Response = await fetchImplementation(buildRequestUrl(endpoint), {
                    method: 'GET',
                    credentials: 'include',
                    headers: { ...(getRequestHeaders ? getRequestHeaders() : {}), ...headers },
                    redirect: 'error',
                });
                try {
                    if (onResponse) {
                        onResponse(response);
                    }
                    if (!response.ok) {
                        // Same refusal mapping as every JSON route, so users see the safe message.
                        const responseBody: string = await response.text();
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            extractErrorMessage(responseBody, response, response.status),
                            parseErrorBody(responseBody, response),
                            responseBody
                        );
                    }
                    return new Uint8Array(await response.arrayBuffer());
                } finally {
                    await cancelUnreadPillarApiResponse(response);
                }
            }),
        getLatestRelease: (params: SambaLatestReleaseQuery, headers?: HttpRequestHeaders) => {
            const queryParams: URLSearchParams = new URLSearchParams({
                platform: params.platform,
                arch: params.arch,
            });
            return fetchPillarAPI<SambaLatestReleaseResponse>(
                `/samba/latest?${queryParams.toString()}`,
                SambaLatestReleaseResponseSchema,
                {
                    headers,
                }
            );
        },
        getInstallerQa: (params: SambaInstallerQaQuery, headers?: HttpRequestHeaders) => {
            const query = SambaInstallerQaQuerySchema.parse(params);
            const queryParams = new URLSearchParams({
                track: query.track,
                arch: query.arch,
            });
            return fetchPillarAPI<WindowsArtifactQa>(
                `/samba/installer-qa?${queryParams.toString()}`,
                WindowsArtifactQaSchema,
                { headers }
            );
        },
    };

    const diagnosticsApi = {
        getEnvironmentVariables: () =>
            fetchPillarAPI<Record<string, string>>(
                '/portal/v1/diagnostics/superadmin/env',
                EnvironmentVariablesResponseSchema
            ),

        getAwsCredentials: () =>
            fetchPillarAPI<AwsCredentialsResponse>(
                '/portal/v1/diagnostics/superadmin/aws-credentials',
                AwsCredentialsResponseSchema
            ),

        getCallerIdentity: () =>
            fetchPillarAPI<GetCallerIdentityResponse>(
                '/portal/v1/diagnostics/superadmin/aws-caller-identity',
                GetCallerIdentityResponseSchema
            ),

        getSystemInfo: () =>
            fetchPillarAPI<GetSystemInfoResponse>(
                '/portal/v1/diagnostics/superadmin/system-info',
                GetSystemInfoResponseSchema
            ),

        getSesDiagnostics: () =>
            fetchPillarAPI<GetSesDiagnosticsResponse>(
                '/portal/v1/diagnostics/superadmin/ses',
                GetSesDiagnosticsResponseSchema
            ),
    };

    const encryptionKeysApi = {
        listKeys: (query?: ListEncryptionKeysRequest) => {
            const parsedQuery =
                query === undefined ? undefined : ListEncryptionKeysRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpoint: string =
                serializedQuery.length > 0
                    ? `/portal/v1/encryption_keys?${serializedQuery}`
                    : '/portal/v1/encryption_keys';
            return fetchPillarAPI<ListEncryptionKeysResponse>(
                endpoint,
                ListEncryptionKeysResponseSchema
            );
        },

        rotateCurrentKey: dedupe(() =>
            fetchPillarAPI<RotateEncryptionKeyResponse>(
                '/portal/v1/encryption_keys/rotate',
                RotateEncryptionKeyResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        rekeyStaleUsage: dedupe((request?: RekeyEncryptionKeyUsageRequest) =>
            fetchPillarAPI<RekeyEncryptionKeyUsageResponse>(
                '/portal/v1/encryption_keys/rekey_stale_usage',
                RekeyEncryptionKeyUsageResponseSchema,
                {
                    method: 'POST',
                    body:
                        request === undefined
                            ? {}
                            : RekeyEncryptionKeyUsageRequestSchema.parse(request),
                }
            )
        ),

        listCredentials: (secretName: string, query?: ListEncryptionKeyCredentialsRequest) => {
            const parsedQuery =
                query === undefined
                    ? undefined
                    : ListEncryptionKeyCredentialsRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpointBase = `/portal/v1/encryption_keys/${encodeURIComponent(
                secretName
            )}/credentials`;
            const endpoint =
                serializedQuery.length > 0 ? `${endpointBase}?${serializedQuery}` : endpointBase;
            return fetchPillarAPI<ListEncryptionKeyCredentialsResponse>(
                endpoint,
                ListEncryptionKeyCredentialsResponseSchema
            );
        },

        listCredentialRotationAttempts: (
            secretName: string,
            query?: ListEncryptionKeyCredentialRotationAttemptsRequest
        ) => {
            const parsedQuery =
                query === undefined
                    ? undefined
                    : ListEncryptionKeyCredentialRotationAttemptsRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpointBase = `/portal/v1/encryption_keys/${encodeURIComponent(
                secretName
            )}/credential_rotation_attempts`;
            const endpoint =
                serializedQuery.length > 0 ? `${endpointBase}?${serializedQuery}` : endpointBase;
            return fetchPillarAPI<ListEncryptionKeyCredentialRotationAttemptsResponse>(
                endpoint,
                ListEncryptionKeyCredentialRotationAttemptsResponseSchema
            );
        },
    };

    const secretsApi = {
        listSecrets: (
            organization: SecretOrganizationReference | SecretOrganizationUuidReference,
            query?: ListSecretsForOrganizationQuery
        ) => {
            const parsedQuery: ListSecretsForOrganizationQuery | undefined =
                query !== undefined
                    ? ListSecretsForOrganizationQuerySchema.parse(query)
                    : undefined;
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.actions !== undefined) {
                for (const action of parsedQuery.actions) {
                    searchParams.append('actions', action);
                }
            }
            if (parsedQuery?.credential_type !== undefined) {
                searchParams.set('credential_type', parsedQuery.credential_type);
            }
            if (parsedQuery?.credential_uuids !== undefined) {
                for (const credentialUuid of parsedQuery.credential_uuids) {
                    searchParams.append('credential_uuids', credentialUuid);
                }
            }
            if (parsedQuery?.terms !== undefined) {
                for (const term of parsedQuery.terms) {
                    searchParams.append('terms', term);
                }
            }
            if (parsedQuery?.include_descendants !== undefined) {
                searchParams.set(
                    'include_descendants',
                    parsedQuery.include_descendants ? 'true' : 'false'
                );
            }
            if (parsedQuery?.include_inherited !== undefined) {
                searchParams.set(
                    'include_inherited',
                    parsedQuery.include_inherited ? 'true' : 'false'
                );
            }
            if (parsedQuery?.show_deleted === true) {
                searchParams.set('show_deleted', 'true');
            }
            if (parsedQuery?.system_ids !== undefined) {
                for (const systemId of parsedQuery.system_ids) {
                    searchParams.append('system_ids', String(systemId));
                }
            }
            if (parsedQuery?.exclude_policy_linked === true) {
                searchParams.set('exclude_policy_linked', 'true');
            }
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const organizationReferenceAttempt =
                SecretOrganizationReferenceSchema.safeParse(organization);
            const organizationUuidReferenceAttempt =
                SecretOrganizationUuidReferenceSchema.safeParse(organization);
            const organizationUuid: string = organizationReferenceAttempt.success
                ? organizationReferenceAttempt.data.organization_uuid
                : organizationUuidReferenceAttempt.success
                  ? organizationUuidReferenceAttempt.data.organization_uuid
                  : z.string().uuid().parse(organization);
            const serializedQuery: string = searchParams.toString();
            const endpoint: string =
                serializedQuery.length > 0
                    ? `/portal/v1/secrets/organization/${organizationUuid}?${serializedQuery}`
                    : `/portal/v1/secrets/organization/${organizationUuid}`;
            return fetchPillarAPI<ListSecretsResponse>(endpoint, ListSecretsResponseSchema);
        },

        getSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<GetSecretResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}`,
                GetSecretResponseSchema
            ),

        getSecretWithCredentials: (organizationUuid: string, systemId: number, accountId: string) =>
            fetchPillarAPI<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/credentials`,
                SecretWithCredentialsResponseSchema
            ),

        getSecretForUse: (organizationUuid: string, systemId: number, accountId: string) =>
            fetchPillarAPI<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/use-credentials`,
                SecretWithCredentialsResponseSchema
            ),

        listCredentialDefinitions: (query: ListSecretCredentialDefinitionsQuery) => {
            const parsedQuery = ListSecretCredentialDefinitionsQuerySchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            searchParams.set('organization_uuid', parsedQuery.organization_uuid);
            for (const systemId of parsedQuery.system_ids ?? []) {
                searchParams.append('system_ids', String(systemId));
            }
            for (const credentialType of parsedQuery.credential_types ?? []) {
                searchParams.append('credential_types', credentialType);
            }
            return fetchPillarAPI<ListSecretCredentialDefinitionsResponse>(
                `/portal/v1/secrets/credential-definitions?${searchParams.toString()}`,
                ListSecretCredentialDefinitionsResponseSchema
            );
        },

        createSecret: dedupe((data: CreateSecretRequest) =>
            fetchPillarAPI<CreateSecretResponse>('/portal/v1/secrets', CreateSecretResponseSchema, {
                method: 'POST',
                body: CreateSecretRequestSchema.parse(data),
            })
        ),

        testCredential: (request: CredentialTestRequest) =>
            fetchPillarAPI<CredentialTestResult>(
                '/portal/v1/secrets/test',
                CredentialTestResultSchema,
                {
                    method: 'POST',
                    body: CredentialTestRequestSchema.parse(request),
                }
            ),

        updateSecretByAccount: dedupe(
            (
                organizationUuid: string,
                systemId: number,
                accountId: string,
                data: UpdateSecretRequest
            ) =>
                fetchPillarAPI<SecretMetadataResponse>(
                    `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}`,
                    SecretMetadataResponseSchema,
                    {
                        method: 'PUT',
                        body: data,
                    }
                )
        ),

        updateSecret: dedupe(
            (secret: SecretIdentityReference | SecretUuidReference, data: UpdateSecretRequest) =>
                fetchPillarAPI<SecretMetadataResponse>(
                    `/portal/v1/secrets/${(() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()}`,
                    SecretMetadataResponseSchema,
                    {
                        method: 'PATCH',
                        body: data,
                    }
                )
        ),

        disableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/disable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        enableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/enable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        deleteSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listCredentialUsages: (secret: SecretIdentityReference | SecretUuidReference) =>
            fetchPillarAPI<CredentialUsagesResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/usages`,
                CredentialUsagesResponseSchema
            ),

        disconnectCredentialUsages: (
            secret: SecretIdentityReference | SecretUuidReference,
            data: DisconnectCredentialUsagesRequest
        ) =>
            fetchPillarAPI<DisconnectCredentialUsagesResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/disconnect`,
                DisconnectCredentialUsagesResponseSchema,
                {
                    method: 'POST',
                    body: DisconnectCredentialUsagesRequestSchema.parse(data),
                }
            ),
    };

    const rpaCredentialInjectionApi = {
        bootstrap: dedupe((data: IpxCredentialRuntimeBootstrapRequest) =>
            fetchPillarAPI<IpxCredentialRuntimeBootstrapResponse>(
                '/portal/v1/rpa-credential-injection/bootstrap',
                IpxCredentialRuntimeBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeBootstrapRequestSchema.parse(data),
                }
            )
        ),
        resolve: dedupe((data: IpxCredentialRuntimeResolveRequest) =>
            fetchPillarAPI<IpxCredentialRuntimeResolveResponse>(
                '/portal/v1/rpa-credential-injection/resolve',
                IpxCredentialRuntimeResolveResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeResolveRequestSchema.parse(data),
                }
            )
        ),
        rotationBegin: dedupe((data: CredentialRotationBeginRequest) =>
            fetchPillarAPI<CredentialRotationBeginResponse>(
                '/portal/v1/rpa-credential-injection/rotation/begin',
                CredentialRotationBeginResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationBeginRequestSchema.parse(data),
                }
            )
        ),
        rotationInjected: dedupe((data: CredentialRotationInjectedRequest) =>
            fetchPillarAPI<void>(
                '/portal/v1/rpa-credential-injection/rotation/injected',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationInjectedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationSubmitted: dedupe((data: CredentialRotationSubmittedRequest) =>
            fetchPillarAPI<void>(
                '/portal/v1/rpa-credential-injection/rotation/submitted',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationSubmittedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationPromote: dedupe((data: CredentialRotationPromoteRequest) =>
            fetchPillarAPI<CredentialRotationPromoteResponse>(
                '/portal/v1/rpa-credential-injection/rotation/promote',
                CredentialRotationPromoteResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationPromoteRequestSchema.parse(data),
                }
            )
        ),
        rotationFail: dedupe((data: CredentialRotationFailRequest) =>
            fetchPillarAPI<void>(
                '/portal/v1/rpa-credential-injection/rotation/fail',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationFailRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        manualCapture: dedupe((data: ManualCredentialCaptureRequest) =>
            fetchPillarAPI<ManualCredentialCaptureResponse>(
                '/portal/v1/rpa-credential-injection/manual-capture',
                ManualCredentialCaptureResponseSchema,
                {
                    method: 'POST',
                    body: ManualCredentialCaptureRequestSchema.parse(data),
                }
            )
        ),
    };

    const ipxCredentialManagementApi = {
        searchCredentialDefinitions: dedupe((data: IpxCredentialDefinitionSearchRequest) =>
            fetchPillarAPI<IpxCredentialDefinitionSearchResponse>(
                '/portal/v1/ipx-credential-management/credential-definitions/search',
                IpxCredentialDefinitionSearchResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialDefinitionSearchRequestSchema.parse(data),
                }
            )
        ),

        searchOrganizationCredentialPolicyDefinitions: dedupe(
            (data: SearchOrganizationIpxCredentialPolicyDefinitionsRequest) => {
                const requestBody: SearchOrganizationIpxCredentialPolicyDefinitionsRequest =
                    SearchOrganizationIpxCredentialPolicyDefinitionsRequestSchema.parse(data);
                return fetchPillarAPI<SearchOrganizationIpxCredentialPolicyDefinitionsResponse>(
                    `/portal/v1/ipx-credential-management/organizations/${requestBody.organization_uuid}/policy-credential-definitions/search`,
                    SearchOrganizationIpxCredentialPolicyDefinitionsResponseSchema,
                    {
                        method: 'POST',
                        body: IpxCredentialDefinitionSearchRequestSchema.parse({
                            terms: requestBody.terms,
                            limit: requestBody.limit,
                            cursor: requestBody.cursor,
                            status: requestBody.status,
                            ipx_system_id: requestBody.ipx_system_id,
                        }),
                    }
                );
            }
        ),

        listOrganizationCredentialPolicies: (data: ListOrganizationIpxCredentialPoliciesRequest) =>
            fetchPillarAPI<ListOrganizationIpxCredentialPoliciesResponse>(
                buildIpxCredentialManagementOrganizationPoliciesEndpoint(data),
                ListOrganizationIpxCredentialPoliciesResponseSchema
            ),

        listDashboardWebsiteCredentials: (data: ListDashboardWebsiteCredentialsRequest) =>
            fetchPillarAPI<ListDashboardWebsiteCredentialsResponse>(
                buildIpxCredentialManagementDashboardWebsiteCredentialsEndpoint(data),
                ListDashboardWebsiteCredentialsResponseSchema
            ),

        createDashboardWebsiteCredential: dedupe(
            (data: CreateDashboardWebsiteCredentialRequest) => {
                const requestBody: CreateDashboardWebsiteCredentialRequest =
                    CreateDashboardWebsiteCredentialRequestSchema.parse(data);
                return fetchPillarAPI<IpxCredentialSummaryListItem>(
                    buildIpxCredentialManagementDashboardWebsiteCredentialEndpoint({
                        organization_uuid: requestBody.organization_uuid,
                        organization_ipx_credential_policy_uuid:
                            requestBody.organization_ipx_credential_policy_uuid,
                    }),
                    IpxCredentialSummaryListItemSchema,
                    {
                        method: 'POST',
                        body: {
                            account_id: requestBody.account_id,
                            edited_fields: requestBody.edited_fields,
                        },
                    }
                );
            }
        ),

        createOrganizationCredentialPolicy: dedupe(
            (data: CreateOrganizationIpxCredentialPolicyRequest) => {
                const requestBody: CreateOrganizationIpxCredentialPolicyRequest =
                    CreateOrganizationIpxCredentialPolicyRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementOrganizationPoliciesEndpoint({
                        organization_uuid: requestBody.organization_uuid,
                    }),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                        body: {
                            ipx_credential_definition_uuid:
                                requestBody.ipx_credential_definition_uuid,
                        },
                    }
                );
            }
        ),

        disableOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/disable'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        enableOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/enable'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        deleteOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'DELETE',
                    }
                );
            }
        ),

        restoreOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/restore'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        listCredentialsForPolicy: (policyUuid: string) =>
            fetchPillarAPI<ListIpxCredentialsForPolicyResponse>(
                buildIpxCredentialManagementPolicyEndpoint(policyUuid, '/credentials'),
                ListIpxCredentialsForPolicyResponseSchema
            ),

        createCredentialForPolicy: dedupe((policyUuid: string, data: CreateIpxCredentialRequest) =>
            fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementPolicyEndpoint(policyUuid, '/credentials'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                    body: CreateIpxCredentialRequestSchema.parse(data),
                }
            )
        ),

        updatePolicyCredential: dedupe((data: UpdateIpxCredentialRequest) => {
            const requestBody: UpdateIpxCredentialRequest =
                UpdateIpxCredentialRequestSchema.parse(data);
            return fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(requestBody.credential_uuid),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'PATCH',
                    body: {
                        account_id: requestBody.account_id,
                        edited_fields: requestBody.edited_fields,
                    },
                }
            );
        }),

        revealPolicyCredentialSecretField: dedupe((data: RevealIpxCredentialSecretFieldRequest) => {
            const requestBody: RevealIpxCredentialSecretFieldRequest =
                RevealIpxCredentialSecretFieldRequestSchema.parse(data);
            return fetchPillarAPI<RevealIpxCredentialSecretFieldResponse>(
                buildIpxCredentialManagementCredentialEndpoint(
                    requestBody.credential_uuid,
                    '/reveal-field'
                ),
                RevealIpxCredentialSecretFieldResponseSchema,
                {
                    method: 'POST',
                    body: {
                        field_name: requestBody.field_name,
                    },
                }
            );
        }),

        disablePolicyCredential: dedupe((credentialUuid: string) =>
            fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/disable'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        enablePolicyCredential: dedupe((credentialUuid: string) =>
            fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/enable'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        deletePolicyCredential: dedupe((credentialUuid: string) =>
            fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'DELETE',
                }
            )
        ),

        restorePolicyCredential: dedupe((credentialUuid: string) =>
            fetchPillarAPI<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/restore'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        listCredentialRotationHistory: (data: CredentialRotationHistoryRequest) =>
            fetchPillarAPI<CredentialRotationHistoryResponse>(
                buildIpxCredentialManagementCredentialRotationHistoryEndpoint(data),
                CredentialRotationHistoryResponseSchema
            ),

        manualPromoteCredentialRotationAttempt: dedupe(
            (data: CredentialRotationManualPromotionRequest) => {
                const requestBody: CredentialRotationManualPromotionRequest =
                    CredentialRotationManualPromotionRequestSchema.parse(data);
                return fetchPillarAPI<CredentialRotationManualPromotionResponse>(
                    `/portal/v1/ipx-credential-management/rotation-attempts/${encodeURIComponent(
                        requestBody.credential_rotation_attempt_uuid
                    )}/manual-promote`,
                    CredentialRotationManualPromotionResponseSchema,
                    {
                        method: 'POST',
                        body: {
                            confirmation: requestBody.confirmation,
                        },
                    }
                );
            }
        ),
    };

    const rpaBuilderApi = {
        bootstrap: dedupe((data: RpaBuilderBootstrapRequest) =>
            fetchPillarAPI<RpaBuilderBootstrapResponse>(
                '/portal/v1/rpa-builder/bootstrap',
                RpaBuilderBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderBootstrapRequestSchema.parse(data),
                }
            )
        ),

        searchAllowedWebsiteTemplates: (params: AllowedWebsiteTemplateSearchRequest = {}) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return fetchPillarAPI<AllowedWebsiteTemplateSearchPageResponse>(
                '/portal/v1/rpa-builder/allowed-website-templates/search',
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getAllowedWebsiteTemplate: (templateUuid: string) =>
            fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                `/portal/v1/rpa-builder/allowed-website-templates/${encodeURIComponent(
                    AllowedWebsiteTemplateUuidRequestSchema.parse({
                        allowed_website_template_uuid: templateUuid,
                    }).allowed_website_template_uuid
                )}`,
                AllowedWebsiteTemplateMutationResponseSchema
            ),

        createAllowedWebsiteTemplate: dedupe((data: CreateAllowedWebsiteTemplateRequest) => {
            const requestBody: CreateAllowedWebsiteTemplateRequest =
                CreateAllowedWebsiteTemplateRequestSchema.parse(data);
            return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                '/portal/v1/rpa-builder/allowed-website-templates',
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        updateAllowedWebsiteTemplate: dedupe(
            (templateUuid: string, data: UpdateAllowedWebsiteTemplateRequest) => {
                const requestData: AllowedWebsiteTemplateUuidRequest =
                    AllowedWebsiteTemplateUuidRequestSchema.parse({
                        allowed_website_template_uuid: templateUuid,
                    });
                const requestBody: UpdateAllowedWebsiteTemplateRequest =
                    UpdateAllowedWebsiteTemplateRequestSchema.parse(data);
                return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                    `/portal/v1/rpa-builder/allowed-website-templates/${encodeURIComponent(
                        requestData.allowed_website_template_uuid
                    )}`,
                    AllowedWebsiteTemplateMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        listCredentialDefinitions: (data: RpaBuilderListCredentialDefinitionsRequest) =>
            fetchPillarAPI<RpaBuilderListCredentialDefinitionsResponse>(
                buildRpaBuilderListCredentialDefinitionsEndpoint(data),
                RpaBuilderListCredentialDefinitionsResponseSchema
            ),

        createCredentialDefinition: dedupe((data: RpaBuilderCreateCredentialDefinitionRequest) =>
            fetchPillarAPI<RpaBuilderSaveCredentialDefinitionResponse>(
                '/portal/v1/rpa-builder/credential-definitions',
                RpaBuilderSaveCredentialDefinitionResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderCreateCredentialDefinitionRequestSchema.parse(data),
                }
            )
        ),

        updateCredentialDefinition: dedupe((data: RpaBuilderUpdateCredentialDefinitionRequest) => {
            const requestBody: RpaBuilderUpdateCredentialDefinitionRequest =
                RpaBuilderUpdateCredentialDefinitionRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderSaveCredentialDefinitionResponse>(
                buildRpaBuilderDefinitionEndpoint(requestBody.ipx_credential_definition_uuid),
                RpaBuilderSaveCredentialDefinitionResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        saveWebsiteCredentialConfiguration: dedupe(
            (data: RpaBuilderSaveWebsiteCredentialConfigurationRequest) =>
                fetchPillarAPI<RpaBuilderSaveWebsiteCredentialConfigurationResponse>(
                    '/portal/v1/rpa-builder/website-configuration/credentials',
                    RpaBuilderSaveWebsiteCredentialConfigurationResponseSchema,
                    {
                        method: 'PUT',
                        body: RpaBuilderSaveWebsiteCredentialConfigurationRequestSchema.parse(data),
                    }
                )
        ),

        deleteCredentialDefinition: dedupe((data: RpaBuilderDeleteCredentialDefinitionRequest) => {
            const requestData: RpaBuilderDeleteCredentialDefinitionRequest =
                RpaBuilderDeleteCredentialDefinitionRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderDeleteCredentialDefinitionResponse>(
                buildRpaBuilderDefinitionEndpoint(requestData.ipx_credential_definition_uuid),
                RpaBuilderDeleteCredentialDefinitionResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),

        resetInvalidLoginWorkflow: dedupe((data: RpaBuilderResetInvalidLoginWorkflowRequest) => {
            const requestData: RpaBuilderResetInvalidLoginWorkflowRequest =
                RpaBuilderResetInvalidLoginWorkflowRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderResetInvalidLoginWorkflowResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestData.ipx_credential_definition_uuid,
                    '/reset-invalid-login-workflow'
                ),
                RpaBuilderResetInvalidLoginWorkflowResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        attachCredentialPolicy: dedupe((data: RpaBuilderAttachCredentialPolicyRequest) => {
            const requestBody: RpaBuilderAttachCredentialPolicyRequest =
                RpaBuilderAttachCredentialPolicyRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderAttachCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy'
                ),
                RpaBuilderAttachCredentialPolicyResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        reactivateCredentialPolicy: dedupe((data: RpaBuilderReactivateCredentialPolicyRequest) => {
            const requestBody: RpaBuilderReactivateCredentialPolicyRequest =
                RpaBuilderReactivateCredentialPolicyRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderReactivateCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy'
                ),
                RpaBuilderReactivateCredentialPolicyResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        deactivateCredentialPolicy: dedupe((data: RpaBuilderDeactivateCredentialPolicyRequest) => {
            const requestBody: RpaBuilderDeactivateCredentialPolicyRequest =
                RpaBuilderDeactivateCredentialPolicyRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderDeactivateCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy/deactivate'
                ),
                RpaBuilderDeactivateCredentialPolicyResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        listWebResourceModels: (data: RpaBuilderListWebResourceModelsRequest = {}) =>
            fetchPillarAPI<RpaBuilderListWebResourceModelsResponse>(
                buildRpaBuilderListWebResourceModelsEndpoint(data),
                RpaBuilderListWebResourceModelsResponseSchema
            ),

        fetchWebResourceModel: (data: RpaBuilderFetchWebResourceModelRequest) => {
            const requestData: RpaBuilderFetchWebResourceModelRequest =
                RpaBuilderFetchWebResourceModelRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderFetchWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestData.ipx_web_resource_model_uuid),
                RpaBuilderFetchWebResourceModelResponseSchema
            );
        },

        createWebResourceModel: dedupe((data: RpaBuilderCreateWebResourceModelRequest) =>
            fetchPillarAPI<RpaBuilderSaveWebResourceModelResponse>(
                '/portal/v1/rpa-builder/web-resource-models',
                RpaBuilderSaveWebResourceModelResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderCreateWebResourceModelRequestSchema.parse(data),
                }
            )
        ),

        replaceWebResourceModel: dedupe((data: RpaBuilderReplaceWebResourceModelRequest) => {
            const requestBody: RpaBuilderReplaceWebResourceModelRequest =
                RpaBuilderReplaceWebResourceModelRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderSaveWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestBody.ipx_web_resource_model_uuid),
                RpaBuilderSaveWebResourceModelResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        deleteWebResourceModel: dedupe((data: RpaBuilderDeleteWebResourceModelRequest) => {
            const requestData: RpaBuilderDeleteWebResourceModelRequest =
                RpaBuilderDeleteWebResourceModelRequestSchema.parse(data);
            return fetchPillarAPI<RpaBuilderDeleteWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestData.ipx_web_resource_model_uuid),
                RpaBuilderDeleteWebResourceModelResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),
    };

    const permissionsApi = {
        getRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentPermissionsView>(
                `/portal/v1/permissions/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionsViewSchema
            ),

        auditRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            fetchPillarAPI<RoleAssignmentPermissionAuditResponse>(
                `/portal/v1/permissions/audit/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionAuditResponseSchema
            ),

        auditOrganizationPermissions: (organizationUuid: string) =>
            fetchPillarAPI<OrganizationPermissionAuditResponse>(
                `/portal/v1/permissions/audit/organization/${organizationUuid}`,
                OrganizationPermissionAuditResponseSchema
            ),

        grantPermission: (data: GrantPermissionRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/grant', VoidResponseSchema, {
                method: 'POST',
                body: GrantPermissionRequestSchema.parse(data),
            }),

        revokePermission: (data: RevokePermissionRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/revoke', VoidResponseSchema, {
                method: 'POST',
                body: RevokePermissionRequestSchema.parse(data),
            }),

        repairAuditedPermission: (data: RepairAuditedPermissionRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/audit/repair', VoidResponseSchema, {
                method: 'POST',
                body: RepairAuditedPermissionRequestSchema.parse(data),
            }),

        listSharedBundles: () =>
            fetchPillarAPI<ListPublicBundlesResponse>(
                '/portal/v1/permissions/bundles/shared',
                ListPublicBundlesResponseSchema
            ),

        searchSharedBundles: (params: SharedPermissionBundlesSearchRequest = {}) => {
            const requestBody: SharedPermissionBundlesSearchRequest =
                SharedPermissionBundlesSearchRequestSchema.parse(params);
            return fetchPillarAPI<SharedPermissionBundlesSearchPageResponse>(
                '/portal/v1/permissions/bundles/shared/search',
                SharedPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getBundle: (bundleUuid: string) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createSharedBundle: (data: CreateSharedBundleRequest) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                '/portal/v1/permissions/bundles/shared',
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateBundle: (bundleUuid: string, data: UpdateBundleRequest) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deleteBundle: (bundleUuid: string) =>
            fetchPillarAPI<void>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        assignBundle: (data: AssignBundleRequest) =>
            fetchPillarAPI<void>('/portal/v1/permissions/assign-bundle', VoidResponseSchema, {
                method: 'POST',
                body: AssignBundleRequestSchema.parse(data),
            }),

        searchPolicyPermissionBundlesForOrganization: (
            organizationUuid: string,
            params: PolicyPermissionBundlesSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundlesSearchRequest =
                PolicyPermissionBundlesSearchRequestSchema.parse(params);
            return fetchPillarAPI<PolicyPermissionBundlesSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}/search`,
                PolicyPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPolicyPermissionBundle: (permissionsBundleUuid: string) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createPolicyPermissionBundleForOrganization: (
            organizationUuid: string,
            data: CreatePolicyPermissionBundleRequest
        ) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updatePolicyPermissionBundle: (
            permissionsBundleUuid: string,
            data: UpdatePolicyPermissionBundleRequest
        ) =>
            fetchPillarAPI<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deletePolicyPermissionBundle: (permissionsBundleUuid: string) =>
            fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listPolicyPermissionGrantsForRoleAssignment: (roleAssignmentUuid: string) =>
            fetchPillarAPI<ListPolicyPermissionGrantsResponse>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                ListPolicyPermissionGrantsResponseSchema
            ),

        grantPolicyPermissionBundleToRoleAssignment: (
            roleAssignmentUuid: string,
            data: GrantPolicyPermissionBundleRequest
        ) => {
            const parsedData: GrantPolicyPermissionBundleRequest =
                GrantPolicyPermissionBundleRequestSchema.parse(data);
            return fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parsedData,
                }
            );
        },

        reorderPolicyPermissionGrantsForRoleAssignment: (
            roleAssignmentUuid: string,
            data: ReorderPolicyPermissionGrantsRequest
        ) => {
            const parsedData: ReorderPolicyPermissionGrantsRequest =
                ReorderPolicyPermissionGrantsRequestSchema.parse(data);
            return fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}/reorder`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parsedData,
                }
            );
        },

        revokePolicyPermissionBundleFromRoleAssignment: (
            roleAssignmentUuid: string,
            permissionsBundleUuid: string
        ) =>
            fetchPillarAPI<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        getOrganizationScopedPermissionSourceOptions: (organizationUuid: string) =>
            fetchPillarAPI<OrganizationScopedPermissionSourceOptionsResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/options`,
                OrganizationScopedPermissionSourceOptionsResponseSchema
            ),

        searchRoleAssignmentsForOrganization: (
            organizationUuid: string,
            params: PermissionSourceRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PermissionSourceRoleAssignmentsSearchRequest =
                PermissionSourceRoleAssignmentsSearchRequestSchema.parse(params);
            return fetchPillarAPI<PermissionSourceRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/role-assignments/search`,
                PermissionSourceRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchRoleAssignmentsForPolicyPermissionBundle: (
            organizationUuid: string,
            permissionsBundleUuid: string,
            params: PolicyPermissionBundleRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundleRoleAssignmentsSearchRequest =
                PolicyPermissionBundleRoleAssignmentsSearchRequestSchema.parse(params);
            return fetchPillarAPI<PolicyPermissionBundleRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}/organization/${organizationUuid}/role-assignments/search`,
                PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getRoleEligibility: (role: string) =>
            fetchPillarAPI<RoleEligibility>(
                `/portal/v1/permissions/eligibility/${role}`,
                RoleEligibilitySchema
            ),
    };

    const authorizationApi = {
        canCreateUser: (organizationUuid: string) => {
            const validatedOrganizationUuid: string = z.string().uuid().parse(organizationUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: validatedOrganizationUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_create_user?${queryParams.toString()}`,
                CanCreateUserResponseSchema
            );
        },

        canModifyUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_modify_user?${queryParams.toString()}`,
                CanModifyUserResponseSchema
            );
        },

        canDeleteUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_delete_user?${queryParams.toString()}`,
                CanDeleteUserResponseSchema
            );
        },

        canImpersonateRoleAssignment: (roleAssignmentUuid: string) => {
            const validatedRoleAssignmentUuid: string = z.string().uuid().parse(roleAssignmentUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                role_assignment_uuid: validatedRoleAssignmentUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/can_impersonate_role_assignment?${queryParams.toString()}`,
                CanImpersonateRoleAssignmentResponseSchema
            );
        },

        hasAccessToOrganization: (organizationUuid: string) => {
            const validatedOrganizationUuid: string = z.string().uuid().parse(organizationUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: validatedOrganizationUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/has_access_to_organization?${queryParams.toString()}`,
                HasAccessToOrganizationResponseSchema
            );
        },

        hasAccessToUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return fetchPillarAPI(
                `/portal/v1/authorization/has_access_to_user?${queryParams.toString()}`,
                HasAccessToUserResponseSchema
            );
        },

        getAccessibleOrganizationIds: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organization_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_organization_ids';
            return fetchPillarAPI(url, AccessibleOrganizationIdsResponseSchema);
        },

        getAccessibleOrganizations: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organizations?${queryString}`
                    : '/portal/v1/authorization/accessible_organizations';
            return fetchPillarAPI(url, AccessibleOrganizationsResponseSchema);
        },

        getAccessibleUserIds: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_user_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_user_ids';
            return fetchPillarAPI(url, AccessibleUserIdsResponseSchema);
        },

        getAccessibleUsers: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_users?${queryString}`
                    : '/portal/v1/authorization/accessible_users';
            return fetchPillarAPI(url, AccessibleUsersResponseSchema);
        },

        isAuthorized: (params: IsAuthorizedRequest) => {
            return fetchPillarAPI(
                '/portal/v1/authorization/is_authorized',
                IsAuthorizedResponseSchema,
                {
                    method: 'POST',
                    body: IsAuthorizedRequestSchema.parse(params),
                }
            );
        },
    };

    const legalDocumentsApi = {
        searchLegalDocumentsPage: (params: LegalDocumentsListRequest = {}) => {
            const requestBody: LegalDocumentsListRequest =
                LegalDocumentsListRequestSchema.parse(params);
            return fetchPillarAPI<SearchLegalDocumentsPageResponse>(
                '/portal/v1/legal_documents/search',
                SearchLegalDocumentsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        assignLegalDocument: (data: CreateLegalDocumentAssignmentRequest) =>
            fetchPillarAPI<LegalDocumentAssignmentPublic>(
                '/portal/v1/legal_documents/assignments',
                LegalDocumentAssignmentPublicSchema,
                {
                    method: 'POST',
                    body: CreateLegalDocumentAssignmentRequestSchema.parse(data),
                }
            ),

        searchLegalDocumentAssignmentsPage: (params: LegalDocumentsSearchRequest = {}) =>
            fetchPillarAPI<SearchLegalDocumentAssignmentsPageResponse>(
                '/portal/v1/legal_documents/assignments/search',
                SearchLegalDocumentAssignmentsPageResponseSchema,
                {
                    method: 'POST',
                    body: LegalDocumentsSearchRequestSchema.parse(params),
                }
            ),

        getApplicableTerms: () =>
            fetchPillarAPI<ApplicableTermsResult>(
                '/portal/v1/legal_documents/applicable',
                ApplicableTermsResultSchema
            ),

        acceptApplicableTerms: (data: AcceptApplicableTermsRequest) =>
            fetchPillarAPI<ApplicableTermsResult>(
                '/portal/v1/legal_documents/applicable/accept',
                ApplicableTermsResultSchema,
                {
                    method: 'POST',
                    body: AcceptApplicableTermsRequestSchema.parse(data),
                }
            ),

        searchOrganizationAgreementsPage: (
            organizationUuid: string,
            params: LegalDocumentsSearchRequest = {}
        ) => {
            const parsedOrganizationUuid = z.string().uuid().parse(organizationUuid);
            return fetchPillarAPI<SearchOrganizationAgreementsPageResponse>(
                `/portal/v1/legal_documents/organizations/${parsedOrganizationUuid}/agreements/search`,
                SearchOrganizationAgreementsPageResponseSchema,
                {
                    method: 'POST',
                    body: LegalDocumentsSearchRequestSchema.parse(params),
                }
            );
        },

        acceptOrganizationAgreement: (
            organizationUuid: string,
            data: AcceptOrganizationAgreementRequest
        ) => {
            const parsedOrganizationUuid = z.string().uuid().parse(organizationUuid);
            return fetchPillarAPI<OrganizationAgreementStatus>(
                `/portal/v1/legal_documents/organizations/${parsedOrganizationUuid}/agreements/accept`,
                OrganizationAgreementStatusSchema,
                {
                    method: 'POST',
                    body: AcceptOrganizationAgreementRequestSchema.parse(data),
                }
            );
        },

        createLegalDocument: (data: CreateLegalDocumentRequest) => {
            const parseResult = CreateLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create legal document request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<CreateLegalDocumentResponse>(
                '/portal/v1/legal_documents',
                CreateLegalDocumentResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },

        getLegalDocument: (legalDocumentUuid: string) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            return fetchPillarAPI<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${idParseResult.data}`,
                GetLegalDocumentResponseSchema
            );
        },

        listPendingRequiredLegalDocuments: dedupe(() =>
            fetchPillarAPI<ListPendingRequiredLegalDocumentsResponse>(
                '/portal/v1/legal_documents/pending',
                ListPendingRequiredLegalDocumentsResponseSchema
            )
        ),

        acceptLegalDocument: (legalDocumentUuid: string, data: AcceptLegalDocumentRequest) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            const parseResult = AcceptLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid accept legal document request: ${parseResult.error.message}`
                );
            }
            return fetchPillarAPI<void>(
                `/portal/v1/legal_documents/${idParseResult.data}/accept`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },

        updateLegalDocument: (legalDocumentUuid: string, data: UpdateLegalDocumentRequest) =>
            fetchPillarAPI<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${LegalDocumentUuidParamSchema.parse(legalDocumentUuid)}`,
                GetLegalDocumentResponseSchema,
                {
                    method: 'PUT',
                    body: UpdateLegalDocumentRequestSchema.parse(data),
                }
            ),

        deleteLegalDocument: (legalDocumentUuid: string) =>
            fetchPillarAPI<void>(
                `/portal/v1/legal_documents/${LegalDocumentUuidParamSchema.parse(legalDocumentUuid)}`,
                VoidResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteLegalDocument: (legalDocumentUuid: string) =>
            fetchPillarAPI<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${LegalDocumentUuidParamSchema.parse(legalDocumentUuid)}/undelete`,
                GetLegalDocumentResponseSchema,
                { method: 'POST' }
            ),

        revokeLegalDocumentAssignment: (
            legalDocumentAssignmentUuid: string,
            data: RevokeLegalDocumentAssignmentRequest = {}
        ) =>
            fetchPillarAPI<LegalDocumentAssignmentPublic>(
                `/portal/v1/legal_documents/assignments/${z.string().uuid().parse(legalDocumentAssignmentUuid)}/revoke`,
                LegalDocumentAssignmentPublicSchema,
                {
                    method: 'POST',
                    body: RevokeLegalDocumentAssignmentRequestSchema.parse(data),
                }
            ),

        revokeAllLegalDocumentAssignments: (
            legalDocumentUuid: string,
            data: RevokeAllLegalDocumentAssignmentsRequest = {}
        ) =>
            fetchPillarAPI<RevokeAllLegalDocumentAssignmentsResponse>(
                `/portal/v1/legal_documents/${LegalDocumentUuidParamSchema.parse(legalDocumentUuid)}/revoke_assignments`,
                RevokeAllLegalDocumentAssignmentsResponseSchema,
                {
                    method: 'POST',
                    body: RevokeAllLegalDocumentAssignmentsRequestSchema.parse(data),
                }
            ),

        searchLegalDocumentAcceptancesPage: (
            legalDocumentUuid: string,
            params: LegalDocumentAcceptancesSearchRequest = {}
        ) =>
            fetchPillarAPI<SearchLegalDocumentAcceptancesPageResponse>(
                `/portal/v1/legal_documents/${LegalDocumentUuidParamSchema.parse(legalDocumentUuid)}/acceptances/search`,
                SearchLegalDocumentAcceptancesPageResponseSchema,
                {
                    method: 'POST',
                    body: LegalDocumentAcceptancesSearchRequestSchema.parse(params),
                }
            ),

        getLegalDocumentAcceptance: (
            acceptanceKind: 'user' | 'organization',
            acceptanceUuid: string
        ) =>
            fetchPillarAPI<GetLegalDocumentAcceptanceResponse>(
                `/portal/v1/legal_documents/acceptances/${z.enum(['user', 'organization']).parse(acceptanceKind)}/${z.string().uuid().parse(acceptanceUuid)}`,
                GetLegalDocumentAcceptanceResponseSchema
            ),

        /** Soft-deletes an acceptance; it stays on record but no longer counts. */
        withdrawLegalDocumentAcceptance: (
            acceptanceKind: 'user' | 'organization',
            acceptanceUuid: string
        ) =>
            fetchPillarAPI<GetLegalDocumentAcceptanceResponse>(
                `/portal/v1/legal_documents/acceptances/${z.enum(['user', 'organization']).parse(acceptanceKind)}/${z.string().uuid().parse(acceptanceUuid)}/withdraw`,
                GetLegalDocumentAcceptanceResponseSchema,
                { method: 'POST' }
            ),

        updateLegalDocumentStatus: async (
            legalDocumentUuid: string,
            data: UpdateLegalDocumentStatusRequest
        ) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            const status: unknown = data.status;
            if (status !== 'ACTIVE' && status !== 'DISABLED') {
                throw new Error(
                    'Invalid legal document status update request: status must be ACTIVE or DISABLED'
                );
            }
            return await fetchPillarAPI<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${idParseResult.data}`,
                GetLegalDocumentResponseSchema,
                {
                    method: 'PATCH',
                    body: { status },
                }
            );
        },
    };

    const legalAgreementsAdminApi = {
        previewImpact: (request: LegalAgreementImpactRequest) =>
            fetchPillarAPI<LegalAgreementImpact>(
                '/portal/v1/legal_agreements/impact',
                LegalAgreementImpactSchema,
                {
                    method: 'POST',
                    body: LegalAgreementImpactRequestSchema.parse(request),
                }
            ),
        getConfigurationHealth: () =>
            fetchPillarAPI<LegalConfigurationHealth>(
                '/portal/v1/legal_agreements/configuration_health',
                LegalConfigurationHealthSchema
            ),
    };

    const myLegalAgreementsApi = {
        searchMyLegalAgreementsPage: (params: MyLegalAgreementsSearchRequest = {}) =>
            fetchPillarAPI<SearchMyLegalAgreementsPageResponse>(
                '/portal/v1/my_legal_agreements/search',
                SearchMyLegalAgreementsPageResponseSchema,
                {
                    method: 'POST',
                    body: MyLegalAgreementsSearchRequestSchema.parse(params),
                }
            ),

        getMyLegalAgreement: (acceptanceKind: 'user' | 'organization', acceptanceUuid: string) =>
            fetchPillarAPI<GetMyLegalAgreementResponse>(
                `/portal/v1/my_legal_agreements/${z.enum(['user', 'organization']).parse(acceptanceKind)}/${z.string().uuid().parse(acceptanceUuid)}`,
                GetMyLegalAgreementResponseSchema
            ),
    };

    const legalReportingApi = {
        searchOutstandingSignaturesPage: (params: OutstandingSignaturesSearchRequest = {}) =>
            fetchPillarAPI<SearchOutstandingSignaturesPageResponse>(
                '/portal/v1/legal_reports/outstanding_signatures/search',
                SearchOutstandingSignaturesPageResponseSchema,
                {
                    method: 'POST',
                    body: OutstandingSignaturesSearchRequestSchema.parse(params),
                }
            ),
    };

    const auditApi = {
        captureCredentialBaseline: () =>
            fetchPillarAPI<CaptureCredentialAuditBaselineResponse>(
                '/portal/v1/audit/credentials/baseline',
                CaptureCredentialAuditBaselineResponseSchema,
                { method: 'POST' }
            ),
        revealHistoricalCredentialField: (request: RevealHistoricalCredentialFieldRequest) =>
            fetchPillarAPI<RevealHistoricalCredentialFieldResponse>(
                '/portal/v1/audit/credentials/reveal',
                RevealHistoricalCredentialFieldResponseSchema,
                {
                    method: 'POST',
                    body: RevealHistoricalCredentialFieldRequestSchema.parse(request),
                }
            ),
        listTransactionLogPage: (params: TransactionLogSearchRequest = {}) => {
            const requestBody: TransactionLogSearchRequest =
                TransactionLogSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListTransactionLogPageResponse>(
                '/portal/v1/audit/transaction_log/search',
                ListTransactionLogPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
        listTransactionLogDisplayPage: (params: TransactionLogSearchRequest = {}) => {
            const requestBody: TransactionLogSearchRequest =
                TransactionLogSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListTransactionLogDisplayPageResponse>(
                '/portal/v1/audit/transaction_log/search',
                ListTransactionLogDisplayPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
        listAuthorOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListForensicReferenceOptionsPageResponse>(
                '/portal/v1/audit/options/authors/search',
                ListForensicReferenceOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listOrganizationOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListForensicReferenceOptionsPageResponse>(
                '/portal/v1/audit/options/organizations/search',
                ListForensicReferenceOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listSessionOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return fetchPillarAPI<ListForensicSessionOptionsPageResponse>(
                '/portal/v1/audit/options/sessions/search',
                ListForensicSessionOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listRecentUserActivityPage: (params: RecentUserActivitySearchRequest = {}) => {
            const requestBody = RecentUserActivitySearchRequestSchema.parse(params);
            return fetchPillarAPI<ListRecentUserActivityPageResponse>(
                '/portal/v1/audit/recent-activity/users/search',
                ListRecentUserActivityPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listRecentRoleActivityPage: (params: RecentRoleActivitySearchRequest = {}) => {
            const requestBody = RecentRoleActivitySearchRequestSchema.parse(params);
            return fetchPillarAPI<ListRecentRoleActivityPageResponse>(
                '/portal/v1/audit/recent-activity/roles/search',
                ListRecentRoleActivityPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
    };

    const operationalApi = {
        getHealth: (headers?: HttpRequestHeaders) =>
            fetchPillarAPI<HealthResponse>('/health', HealthResponseSchema, {
                headers,
                handle401AsSessionExpired: false,
                acceptedStatusCodes: [503],
            }),
    };

    const securityApi = {
        reportCspViolation: (report: CspViolationReportPayload, headers?: HttpRequestHeaders) => {
            const requestBody: CspViolationReportPayload = CspViolationReportSchema.parse(report);
            return fetchPillarAPI<void>('/portal/v1/security/csp-violation', VoidResponseSchema, {
                method: 'POST',
                body: requestBody,
                headers,
                handle401AsSessionExpired: false,
            });
        },
    };

    const paymentWebhookApi = {
        postTwilioStatusCallback: (
            payload: TwilioStatusCallbackPayload,
            headers?: HttpRequestHeaders
        ) => {
            const requestBody: TwilioStatusCallbackPayload =
                TwilioStatusCallbackPayloadSchema.parse(payload);
            return fetchPillarAPI<void>(
                '/portal/v1/payments/webhooks/twilio/status-callback',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                    headers,
                    handle401AsSessionExpired: false,
                }
            );
        },

        postSendgridEventWebhook: (
            payload: SendgridEventWebhookPayload,
            headers?: HttpRequestHeaders
        ) => {
            const requestBody: SendgridEventWebhookPayload =
                SendgridEventWebhookPayloadSchema.parse(payload);
            return fetchPillarAPI<void>(
                '/portal/v1/payments/webhooks/sendgrid/event-webhook',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                    headers,
                    handle401AsSessionExpired: false,
                }
            );
        },
    };

    const allowedWebsiteTemplatesApi = {
        searchTemplates: (params: AllowedWebsiteTemplateSearchRequest = {}) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return fetchPillarAPI<AllowedWebsiteTemplateSearchPageResponse>(
                '/portal/v1/allowed_website_templates/search',
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getTemplate: (templateUuid: string) =>
            fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(templateUuid),
                AllowedWebsiteTemplateMutationResponseSchema
            ),

        createTemplate: dedupe((data: CreateAllowedWebsiteTemplateRequest) => {
            const requestBody: CreateAllowedWebsiteTemplateRequest =
                CreateAllowedWebsiteTemplateRequestSchema.parse(data);
            return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                '/portal/v1/allowed_website_templates',
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        updateTemplate: dedupe(
            (templateUuid: string, data: UpdateAllowedWebsiteTemplateRequest) => {
                const requestBody: UpdateAllowedWebsiteTemplateRequest =
                    UpdateAllowedWebsiteTemplateRequestSchema.parse(data);
                return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                    buildAllowedWebsiteTemplateEndpoint(templateUuid),
                    AllowedWebsiteTemplateMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        enableTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(
                    requestData.allowed_website_template_uuid,
                    '/enable'
                ),
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        disableTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return fetchPillarAPI<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(
                    requestData.allowed_website_template_uuid,
                    '/disable'
                ),
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        deleteTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return fetchPillarAPI<SafeDeleteAllowedWebsiteTemplateResponse>(
                buildAllowedWebsiteTemplateEndpoint(requestData.allowed_website_template_uuid),
                SafeDeleteAllowedWebsiteTemplateResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),

        getBlastRadius: (templateUuid: string) =>
            fetchPillarAPI<AllowedWebsiteTemplateBlastRadiusResponse>(
                buildAllowedWebsiteTemplateEndpoint(templateUuid, '/blast_radius'),
                AllowedWebsiteTemplateBlastRadiusResponseSchema
            ),

        listOrganizationWebsites: (organizationUuid: string) =>
            fetchPillarAPI<ListOrganizationAllowedWebsitesResponse>(
                buildOrganizationAllowedWebsitesEndpoint(organizationUuid),
                ListOrganizationAllowedWebsitesResponseSchema
            ),

        searchUsableTemplatesForOrganization: (
            organizationUuid: string,
            params: AllowedWebsiteTemplateSearchRequest = {}
        ) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return fetchPillarAPI<AllowedWebsiteTemplateSearchPageResponse>(
                buildOrganizationAllowedWebsitesEndpoint(
                    organizationUuid,
                    '/usable_templates/search'
                ),
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        createLocalOrganizationWebsite: dedupe(
            (organizationUuid: string, data: CreateLocalOrganizationAllowedWebsiteRequest) => {
                const requestBody: CreateLocalOrganizationAllowedWebsiteRequest =
                    CreateLocalOrganizationAllowedWebsiteRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsitesEndpoint(organizationUuid, '/local'),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),

        referenceTemplateForOrganization: dedupe(
            (
                organizationUuid: string,
                data: ReferenceAllowedWebsiteTemplateToOrganizationRequest
            ) => {
                const requestBody: ReferenceAllowedWebsiteTemplateToOrganizationRequest =
                    ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsitesEndpoint(
                        organizationUuid,
                        '/template_reference'
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),

        updateOrganizationWebsite: dedupe(
            (
                organizationUuid: string,
                organizationAllowedWebsiteUuid: string,
                data: UpdateOrganizationAllowedWebsiteRequest
            ) => {
                const requestBody: UpdateOrganizationAllowedWebsiteRequest =
                    UpdateOrganizationAllowedWebsiteRequestSchema.parse(data);
                return fetchPillarAPI<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        deleteOrganizationWebsite: dedupe(
            (organizationUuid: string, organizationAllowedWebsiteUuid: string) =>
                fetchPillarAPI<DeleteOrganizationAllowedWebsiteResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid
                    ),
                    DeleteOrganizationAllowedWebsiteResponseSchema,
                    {
                        method: 'DELETE',
                    }
                )
        ),

        customizeTemplateReferenceForOrganization: dedupe(
            (organizationUuid: string, organizationAllowedWebsiteUuid: string) =>
                fetchPillarAPI<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid,
                        '/customize_template'
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                    }
                )
        ),

        promoteOrganizationWebsiteToTemplate: dedupe(
            (organizationUuid: string, data: PromoteOrganizationWebsiteToTemplateRequest) => {
                const requestBody: PromoteOrganizationWebsiteToTemplateRequest =
                    PromoteOrganizationWebsiteToTemplateRequestSchema.parse(data);
                return fetchPillarAPI<PromoteOrganizationWebsiteToTemplateResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        requestBody.organization_allowed_website_uuid,
                        '/promote_to_template'
                    ),
                    PromoteOrganizationWebsiteToTemplateResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),
    };

    const loggingApi = {
        ingestLogs: (data: LogIngestRequest) => {
            const requestBody: LogIngestRequest = LogIngestRequestSchema.parse(data);
            return fetchPillarAPI<void>('/portal/v1/logs', VoidResponseSchema, {
                method: 'POST',
                body: requestBody,
            });
        },
        listCloudLoggingEntriesPage: (data: CloudLoggingQueryRequest) => {
            const requestBody: CloudLoggingQueryRequest =
                CloudLoggingQueryRequestSchema.parse(data);
            return fetchPillarAPI<ListCloudLoggingEntriesPageResponse>(
                '/portal/v1/logs/search',
                ListCloudLoggingEntriesPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
        listCloudLoggingLogIdOptionsPage: (data: CloudLoggingOptionSearchRequest = {}) => {
            const requestBody: CloudLoggingOptionSearchRequest =
                CloudLoggingOptionSearchRequestSchema.parse(data);
            return fetchPillarAPI<ListCloudLoggingOptionsPageResponse>(
                '/portal/v1/logs/options/log-names/search',
                ListCloudLoggingOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listCloudLoggingResourceTypeOptionsPage: (data: CloudLoggingOptionSearchRequest = {}) => {
            const requestBody: CloudLoggingOptionSearchRequest =
                CloudLoggingOptionSearchRequestSchema.parse(data);
            return fetchPillarAPI<ListCloudLoggingOptionsPageResponse>(
                '/portal/v1/logs/options/resource-types/search',
                ListCloudLoggingOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
    };

    const appApi = {};

    return {
        baseUrl,
        requestWithSchema,
        authApi,
        userApi,
        organizationApi,
        customerApi,
        paymentsInfrastructureApi,
        sambaConfigApi,
        paymentsApi,
        sambaApi,
        diagnosticsApi,
        encryptionKeysApi,
        secretsApi,
        rpaCredentialInjectionApi,
        ipxCredentialManagementApi,
        rpaBuilderApi,
        permissionsApi,
        allowedWebsiteTemplatesApi,
        authorizationApi,
        legalDocumentsApi,
        legalAgreementsAdminApi,
        legalReportingApi,
        myLegalAgreementsApi,
        legalSurfacingApi: createLegalSurfacingApi(requestWithSchema),
        auditApi,
        operationalApi,
        securityApi,
        paymentWebhookApi,
        loggingApi,
        appApi,
    };
}

export type PillarApiClient = ReturnType<typeof createPillarApiWithOptions>;
